import React, { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import './skeleton-loaders.css';

// Generic skeleton component
const Skeleton = ({ width = '100%', height = '20px', borderRadius = '4px', className = '' }) => (
  <div
    className={`skeleton ${className}`}
    style={{
      width,
      height,
      borderRadius,
      backgroundColor: 'var(--skeleton-bg, #f0f0f0)',
      backgroundImage: 'linear-gradient(90deg, transparent, var(--skeleton-shine, rgba(255,255,255,0.4)), transparent)',
      backgroundSize: '200px 100%',
      backgroundRepeat: 'no-repeat',
      animation: 'skeleton-loading 1.5s ease-in-out infinite'
    }}
  />
);

// Skeleton for BV items in sidebar
export const BVSkeletonItem = () => (
  <div className="workspace-subnav-item skeleton-item skeleton-workspace-subnav-item">
    <div className="d-flex align-items-center">
      <Skeleton width="8px" height="8px" borderRadius="50%" className="me-2" />
      <Skeleton width="120px" height="16px" />
    </div>
  </div>
);

// Skeleton for agent tabs
export const AgentTabSkeleton = () => (
  <div className="agent-tab-item skeleton-item skeleton-agent-tab-item">
    <Skeleton width="16px" height="16px" className="me-2" />
    <Skeleton width="80px" height="14px" />
  </div>
);

// Skeleton for the entire BVs section
export const BVsSectionSkeleton = ({ count = 3 }) => (
  <div className="workspace-subnav skeleton-workspace-subnav">
    {Array.from({ length: count }, (_, index) => (
      <div key={index} className="agent-nav-group skeleton-agent-nav-group">
        <BVSkeletonItem />
        {/* Show tabs skeleton for first item to simulate active state */}
        {index === 0 && (
          <div className="agent-tabs skeleton-agent-tabs">
            {Array.from({ length: 4 }, (_, tabIndex) => (
              <AgentTabSkeleton key={tabIndex} />
            ))}
          </div>
        )}
      </div>
    ))}
  </div>
);

// Skeleton for workspace switcher
export const WorkspaceSwitcherSkeleton = () => (
  <div className="workspace-switcher-light skeleton-workspace-switcher-light">
    <button className="workspace-switcher-light-trigger skeleton-item skeleton-workspace-switcher-light-trigger" disabled>
      <span className="workspace-switcher-light-name skeleton-workspace-switcher-light-name">
        <Skeleton width="120px" height="16px" />
      </span>
      <div className="dropdown-icon skeleton-dropdown-icon">
        <Skeleton width="16px" height="16px" />
      </div>
    </button>
  </div>
);

// Skeleton for character count component
export const CharacterCountSkeleton = () => (
  <div className="character-count-skeleton skeleton-character-count-skeleton p-3">
    <div className="d-flex justify-content-between align-items-center mb-2">
      <Skeleton width="80px" height="14px" />
      <Skeleton width="60px" height="14px" />
    </div>
    <Skeleton width="100%" height="8px" borderRadius="4px" className="mb-2" />
    <Skeleton width="50%" height="12px" />
  </div>
);

// Skeleton for brand settings form sections - Full width architecture
export const BrandSettingsFormSkeleton = ({ pageHeader = null, section = 'basics' }) => {
  // Render section-specific content
  const renderSectionSkeleton = () => {
    switch (section) {
      case 'personality':
        return (
          <>
            {/* Brand Archetype Section */}
            <div className="mb-4 skeleton-form-group">
              <Skeleton width="140px" height="18px" className="mb-2" />
              <Skeleton width="100%" height="12px" className="mb-3" />
              <div className="radio-card-group skeleton-radio-card-group">
                {Array.from({ length: 6 }, (_, index) => (
                  <div key={index} className="skeleton-radio-card">
                    <Skeleton width="100%" height="80px" borderRadius="8px" />
                  </div>
                ))}
              </div>
            </div>

            {/* Personality Traits Section */}
            <div className="mb-4 skeleton-form-group">
              <Skeleton width="180px" height="18px" className="mb-2" />
              <Skeleton width="100%" height="12px" className="mb-3" />
              <div className="checkbox-card-group skeleton-checkbox-card-group">
                {Array.from({ length: 8 }, (_, index) => (
                  <div key={index} className="skeleton-checkbox-card">
                    <Skeleton width="100%" height="80px" borderRadius="8px" />
                  </div>
                ))}
              </div>
            </div>

            {/* Tone of Voice Section */}
            <div className="mb-4 skeleton-form-group">
              <Skeleton width="120px" height="18px" className="mb-2" />
              <Skeleton width="100%" height="12px" className="mb-3" />
              <div className="radio-card-group skeleton-radio-card-group">
                {Array.from({ length: 5 }, (_, index) => (
                  <div key={index} className="skeleton-radio-card">
                    <Skeleton width="100%" height="80px" borderRadius="8px" />
                  </div>
                ))}
              </div>
            </div>
          </>
        );

      case 'market':
        return (
          <>
            {/* Business Model Section */}
            <div className="mb-4 skeleton-form-group">
              <Skeleton width="140px" height="18px" className="mb-2" />
              <Skeleton width="100%" height="12px" className="mb-3" />
              <div className="radio-card-group skeleton-radio-card-group">
                {Array.from({ length: 6 }, (_, index) => (
                  <div key={index} className="skeleton-radio-card">
                    <Skeleton width="100%" height="80px" borderRadius="8px" />
                  </div>
                ))}
              </div>
            </div>

            {/* Target Audience Section */}
            <div className="mb-4 skeleton-form-group">
              <Skeleton width="140px" height="18px" className="mb-2" />
              <Skeleton width="100%" height="12px" className="mb-3" />
              <div className="checkbox-card-group skeleton-checkbox-card-group">
                {Array.from({ length: 8 }, (_, index) => (
                  <div key={index} className="skeleton-checkbox-card">
                    <Skeleton width="100%" height="80px" borderRadius="8px" />
                  </div>
                ))}
              </div>
            </div>

            {/* USP Section */}
            <div className="mb-4 skeleton-form-group">
              <Skeleton width="220px" height="18px" className="mb-2" />
              <Skeleton width="100%" height="12px" className="mb-3" />
              <div className="radio-card-group skeleton-radio-card-group">
                {Array.from({ length: 6 }, (_, index) => (
                  <div key={index} className="skeleton-radio-card">
                    <Skeleton width="100%" height="80px" borderRadius="8px" />
                  </div>
                ))}
              </div>
            </div>
          </>
        );

      case 'messaging':
        return (
          <>
            {/* Core Values Section with Show More */}
            <div className="mb-4 skeleton-form-group">
              <Skeleton width="160px" height="18px" className="mb-2" />
              <Skeleton width="100%" height="12px" className="mb-3" />
              <div className="checkbox-card-group skeleton-checkbox-card-group">
                {Array.from({ length: 12 }, (_, index) => (
                  <div key={index} className="skeleton-checkbox-card">
                    <Skeleton width="100%" height="80px" borderRadius="8px" />
                  </div>
                ))}
              </div>
              <div className="text-center mt-3 skeleton-show-more">
                <Skeleton width="120px" height="32px" borderRadius="6px" />
              </div>
            </div>

            {/* Customer Emotions Section with Show More */}
            <div className="mb-4 skeleton-form-group">
              <Skeleton width="220px" height="18px" className="mb-2" />
              <Skeleton width="100%" height="12px" className="mb-3" />
              <div className="checkbox-card-group skeleton-checkbox-card-group">
                {Array.from({ length: 12 }, (_, index) => (
                  <div key={index} className="skeleton-checkbox-card">
                    <Skeleton width="100%" height="80px" borderRadius="8px" />
                  </div>
                ))}
              </div>
              <div className="text-center mt-3 skeleton-show-more">
                <Skeleton width="120px" height="32px" borderRadius="6px" />
              </div>
            </div>

            {/* Tagline Field */}
            <div className="mb-4 skeleton-form-group">
              <Skeleton width="80px" height="18px" className="mb-2" />
              <Skeleton width="100%" height="12px" className="mb-3" />
              <Skeleton width="100%" height="48px" borderRadius="6px" />
            </div>

            {/* Brand Story Field */}
            <div className="mb-4 skeleton-form-group">
              <Skeleton width="160px" height="18px" className="mb-2" />
              <Skeleton width="100%" height="12px" className="mb-3" />
              <Skeleton width="100%" height="96px" borderRadius="6px" />
            </div>
          </>
        );

      case 'basics':
      default:
        return (
          <>
            {/* Company Name Field */}
            <div className="mb-4 skeleton-form-group">
              <Skeleton width="160px" height="18px" className="mb-2" />
              <Skeleton width="100%" height="12px" className="mb-3" />
              <Skeleton width="100%" height="48px" borderRadius="6px" />
            </div>

            {/* Company Description Field */}
            <div className="mb-4 skeleton-form-group">
              <Skeleton width="180px" height="18px" className="mb-2" />
              <Skeleton width="100%" height="12px" className="mb-3" />
              <Skeleton width="100%" height="96px" borderRadius="6px" />
            </div>

            {/* Color and Logo Row */}
            <div className="row skeleton-color-logo-row">
              <div className="col-md-6">
                <div className="mb-4 skeleton-form-group">
                  <Skeleton width="140px" height="18px" className="mb-2" />
                  <Skeleton width="100%" height="12px" className="mb-3" />
                  <Skeleton width="100px" height="100px" borderRadius="6px" />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-4 skeleton-form-group">
                  <Skeleton width="180px" height="18px" className="mb-2" />
                  <Skeleton width="100%" height="12px" className="mb-3" />
                  <div className="skeleton-logo-upload-area">
                    <Skeleton width="100%" height="120px" borderRadius="8px" />
                    <div className="mt-1">
                      <Skeleton width="220px" height="12px" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <div className="brand-settings-container skeleton-brand-settings-container">
      {/* Render actual PageHeader if provided */}
      {pageHeader}

      {/* Main Content */}
      <div className="brand-settings-content skeleton-brand-settings-content">
        <div className="brand-settings-section skeleton-brand-settings-section">
          {renderSectionSkeleton()}
        </div>
      </div>
    </div>
  );
};

// Skeleton for mobile brand settings tabs - Full width architecture
export const BrandSettingsMobileTabsSkeleton = () => (
  <div className="brand-settings-mobile-tabs-skeleton skeleton-brand-settings-mobile-tabs-skeleton">
    <div className="d-flex skeleton-mobile-tabs-container">
      {Array.from({ length: 4 }, (_, index) => (
        <div key={index} className="flex-grow-1 text-center p-3 skeleton-mobile-tab">
          <Skeleton width="36px" height="36px" borderRadius="50%" className="mx-auto mb-2" />
          <Skeleton width="60px" height="12px" className="mx-auto" />
        </div>
      ))}
    </div>
  </div>
);

// Skeleton for account profile tab - Full width architecture
export const AccountProfileTabSkeleton = () => (
  <div className="account-profile-tab-skeleton skeleton-account-profile-tab-skeleton">
    {/* Section header skeleton */}
    <div className="d-flex align-items-center mb-4 border-bottom pb-3 skeleton-section-header">
      <Skeleton width="48px" height="48px" borderRadius="8px" className="me-3" />
      <Skeleton width="180px" height="24px" />
    </div>

    {/* Basic Information Card */}
    <div className="mb-4 p-4 border rounded skeleton-basic-info-card">
      <Skeleton width="140px" height="20px" className="mb-3" />

      {Array.from({ length: 4 }, (_, index) => (
        <div key={index} className="d-flex justify-content-between align-items-center mb-3 skeleton-info-row">
          <Skeleton width="120px" height="16px" />
          <Skeleton width="160px" height="16px" />
        </div>
      ))}
    </div>

    {/* Usage & Limits Card */}
    <div className="mb-4 p-4 border rounded skeleton-usage-limits-card">
      <Skeleton width="140px" height="20px" className="mb-3" />

      {Array.from({ length: 2 }, (_, index) => (
        <div key={index} className="mb-4 skeleton-usage-item">
          <div className="d-flex justify-content-between align-items-center mb-2 skeleton-usage-header">
            <Skeleton width="100px" height="16px" />
            <Skeleton width="40px" height="16px" />
          </div>
          <Skeleton width="100%" height="8px" borderRadius="4px" className="mb-2 skeleton-progress-bar" />
          <div className="d-flex justify-content-between skeleton-usage-details">
            <Skeleton width="60px" height="12px" />
            <Skeleton width="60px" height="12px" />
          </div>
        </div>
      ))}

      <div className="text-center mt-4 skeleton-upgrade-button-container">
        <Skeleton width="160px" height="32px" borderRadius="4px" className="mx-auto" />
      </div>
    </div>

    {/* Subscription Management Card */}
    <div className="p-4 border rounded skeleton-subscription-card">
      <div className="d-flex justify-content-between align-items-center mb-3 skeleton-subscription-header">
        <Skeleton width="180px" height="20px" />
        <Skeleton width="60px" height="24px" borderRadius="12px" />
      </div>

      {Array.from({ length: 3 }, (_, index) => (
        <div key={index} className="d-flex justify-content-between align-items-center mb-3 skeleton-subscription-row">
          <Skeleton width="120px" height="16px" />
          <Skeleton width="80px" height="16px" />
        </div>
      ))}

      <div className="mt-4 skeleton-subscription-action">
        <Skeleton width="120px" height="32px" borderRadius="4px" />
      </div>
    </div>
  </div>
);

// Skeleton for account security tab - Full width architecture
export const AccountSecurityTabSkeleton = () => (
  <div className="account-security-tab-skeleton skeleton-account-security-tab-skeleton">
    {/* Section header skeleton */}
    <div className="d-flex align-items-center mb-4 border-bottom pb-3 skeleton-section-header">
      <Skeleton width="48px" height="48px" borderRadius="8px" className="me-3" />
      <Skeleton width="160px" height="24px" />
    </div>

    {/* Password form card */}
    <div className="p-4 border rounded skeleton-password-form-card">
      <Skeleton width="140px" height="20px" className="mb-2" />
      <Skeleton width="80%" height="14px" className="mb-4 skeleton-form-description" />

      {Array.from({ length: 3 }, (_, index) => (
        <div key={index} className="mb-3 skeleton-password-field">
          <Skeleton width="120px" height="16px" className="mb-2" />
          <Skeleton width="100%" height="40px" borderRadius="4px" />
        </div>
      ))}

      <div className="mt-4 skeleton-password-submit">
        <Skeleton width="140px" height="40px" borderRadius="4px" />
      </div>
    </div>
  </div>
);

// Skeleton for account plans tab - Full width architecture
export const AccountPlansTabSkeleton = () => (
  <div className="account-plans-tab-skeleton skeleton-account-plans-tab-skeleton">
    {/* Section header skeleton */}
    <div className="d-flex align-items-center justify-content-between mb-4 border-bottom pb-3 skeleton-section-header">
      <div className="d-flex align-items-center skeleton-header-left">
        <Skeleton width="48px" height="48px" borderRadius="8px" className="me-3" />
        <Skeleton width="180px" height="24px" />
      </div>
      <Skeleton width="80px" height="24px" borderRadius="12px" className="skeleton-status-badge" />
    </div>

    {/* Billing toggle */}
    <div className="text-center mb-4 skeleton-billing-toggle">
      <div className="d-flex justify-content-center gap-2 skeleton-toggle-buttons">
        <Skeleton width="80px" height="36px" borderRadius="18px" />
        <Skeleton width="80px" height="36px" borderRadius="18px" />
      </div>
    </div>

    {/* Launch promo banner */}
    <div className="mb-4 p-3 border rounded skeleton-promo-banner">
      <Skeleton width="200px" height="20px" className="mx-auto" />
    </div>

    {/* Plan cards */}
    <div className="d-flex gap-3 flex-wrap skeleton-plan-cards">
      {Array.from({ length: 3 }, (_, index) => (
        <div key={index} className="flex-grow-1 skeleton-plan-card-wrapper" style={{ minWidth: '300px' }}>
          <div className="p-4 border rounded skeleton-plan-card">
            {/* Plan header */}
            <div className="text-center mb-3 skeleton-plan-header">
              <Skeleton width="100px" height="20px" className="mx-auto mb-2" />
              <Skeleton width="80px" height="32px" className="mx-auto mb-2" />
              <Skeleton width="60%" height="14px" className="mx-auto" />
            </div>

            {/* Features list */}
            <div className="mb-4 skeleton-features-list">
              {Array.from({ length: 6 }, (_, featureIndex) => (
                <div key={featureIndex} className="d-flex align-items-center mb-2 skeleton-feature-item">
                  <Skeleton width="16px" height="16px" className="me-2" />
                  <Skeleton width="70%" height="14px" />
                </div>
              ))}
            </div>

            {/* CTA button */}
            <Skeleton width="100%" height="40px" borderRadius="4px" className="skeleton-cta-button" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Skeleton for invoice history - Full width architecture
export const InvoiceHistorySkeleton = () => (
  <div className="invoice-history-skeleton skeleton-invoice-history-skeleton mt-4">
    <Skeleton width="120px" height="18px" className="mb-3 skeleton-invoice-title" />

    {/* Invoice table header */}
    <div className="d-flex justify-content-between align-items-center mb-2 skeleton-invoice-header">
      <Skeleton width="60px" height="14px" />
      <Skeleton width="60px" height="14px" />
      <Skeleton width="60px" height="14px" />
    </div>

    {/* Invoice rows */}
    {Array.from({ length: 3 }, (_, index) => (
      <div key={index} className="d-flex justify-content-between align-items-center mb-3 p-2 border-bottom skeleton-invoice-row">
        <div className="skeleton-invoice-details">
          <Skeleton width="80px" height="14px" className="mb-1" />
          <Skeleton width="120px" height="12px" />
        </div>
        <Skeleton width="60px" height="14px" className="skeleton-invoice-amount" />
        <div className="d-flex gap-2 skeleton-invoice-actions">
          <Skeleton width="40px" height="24px" borderRadius="4px" />
          <Skeleton width="40px" height="24px" borderRadius="4px" />
        </div>
      </div>
    ))}
  </div>
);

// Skeleton for workspace management tab - Full width architecture
export const WorkspaceManagementTabSkeleton = () => (
  <div className="workspace-list-view skeleton-workspace-list-view">
    {/* Main Content */}
    <div className="workspace-main-content">
      {/* Workspaces Grid */}
      <div className="workspaces-grid">
        {/* Workspace Cards (3 cards) */}
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className="workspace-card">
            {/* Card Content */}
            <div className="workspace-card-content">
              {/* Header - Workspace name + badges */}
              <div className="workspace-card-header">
                <Skeleton width="60%" height="16px" />
                <div className="workspace-badges">
                  {/* Current badge (only for first card) */}
                  {index === 0 && <Skeleton width="65px" height="20px" borderRadius="12px" />}
                  {/* Role badge */}
                  <Skeleton width="45px" height="20px" borderRadius="12px" />
                </div>
              </div>

              {/* Description */}
              <div className="workspace-description-wrapper">
                <Skeleton width="100%" height="13px" className="mb-1" />
                <Skeleton width="75%" height="13px" />
              </div>

              {/* Stats */}
              <div className="workspace-stats">
                <div className="stat">
                  <Skeleton width="55px" height="12px" className="mb-1" />
                  <Skeleton width="18px" height="14px" />
                </div>
                <div className="stat">
                  <Skeleton width="55px" height="12px" className="mb-1" />
                  <Skeleton width="75px" height="14px" />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="workspace-actions">
              {/* Switch button (only show for non-current workspace) */}
              {index !== 0 && <Skeleton width="70px" height="32px" borderRadius="6px" />}
              {/* Manage button */}
              <Skeleton width="75px" height="32px" borderRadius="6px" />
              {/* Delete button (only show for some cards) */}
              {index === 2 && <Skeleton width="70px" height="32px" borderRadius="6px" />}
            </div>
          </div>
        ))}

        {/* Create New Workspace Card */}
        <div className="create-workspace-card">
          <div className="create-workspace-content">
            {/* Icon */}
            <Skeleton width="48px" height="48px" borderRadius="50%" />
            {/* Text */}
            <div className="create-workspace-text">
              <Skeleton width="170px" height="16px" className="mb-2" />
              <Skeleton width="200px" height="14px" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for invitations tab
export const InvitationsTabSkeleton = () => (
  <div className="workspace-list-view skeleton-invitations-tab-skeleton">
    {/* Tabs Container */}
    <div className="workspace-tabs-container">
      <div className="workspace-tabs">
        <button className="workspace-tab">
          <Skeleton width="110px" height="14px" />
        </button>
        <button className="workspace-tab active">
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Skeleton width="80px" height="14px" />
            <Skeleton width="18px" height="18px" borderRadius="12px" />
          </div>
        </button>
      </div>
    </div>

    {/* Main Content */}
    <div className="workspace-main-content">
      {/* Invitations List */}
      <div className="invitations-list">
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className="invitation-card">
            {/* Invitation Info */}
            <div className="invitation-info">
              {/* Workspace Name */}
              <Skeleton width="200px" height="16px" style={{ marginBottom: '8px' }} />

              {/* Invited by line with role badge */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '4px',
                flexWrap: 'wrap'
              }}>
                <Skeleton width="70px" height="14px" />
                <Skeleton width="150px" height="14px" />
                <Skeleton width="30px" height="14px" />
                <Skeleton width="45px" height="20px" borderRadius="12px" />
              </div>

              {/* Date */}
              <Skeleton width="100px" height="12px" />
            </div>

            {/* Action Buttons */}
            <div className="invitation-actions">
              {/* Accept Button */}
              <Skeleton width="85px" height="40px" borderRadius="8px" />
              {/* Decline Button */}
              <Skeleton width="85px" height="40px" borderRadius="8px" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Skeleton for workspace details
export const WorkspaceDetailsSkeleton = () => (
  <div className="workspace-details-view skeleton-workspace-details-view">
    {/* Back button header */}
    <div className="workspace-details-header">
      <button className="workspace-back-btn">
        <Skeleton width="16px" height="16px" className="me-2" />
        <Skeleton width="140px" height="14px" />
      </button>
    </div>

    {/* Main content */}
    <div className="workspace-details-content">
      {/* Workspace info section */}
      <div className="workspace-info-section">
        <div className="workspace-header-with-edit">
          <div>
            <Skeleton width="200px" height="24px" className="mb-2" />
            <Skeleton width="300px" height="14px" />
          </div>
          <Skeleton width="100px" height="36px" borderRadius="8px" />
        </div>
        <div className="workspace-meta">
          <Skeleton width="140px" height="14px" />
          <Skeleton width="60px" height="20px" borderRadius="12px" />
        </div>
      </div>

      {/* Members section */}
      <div className="members-section">
        <Skeleton width="120px" height="18px" className="mb-3" />

        <div className="members-list">
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="member-item">
              <div className="member-info-wrapper">
                <div className="member-avatar">
                  <div className="avatar-circle">
                    <Skeleton width="40px" height="40px" borderRadius="50%" />
                  </div>
                </div>
                <div className="member-details">
                  <div className="member-primary-info">
                    <Skeleton width="180px" height="15px" />
                    <Skeleton width="50px" height="20px" borderRadius="12px" />
                  </div>
                  <div className="member-meta">
                    <Skeleton width="120px" height="12px" className="mb-1" />
                    <Skeleton width="140px" height="12px" />
                  </div>
                </div>
              </div>
              <div className="member-actions">
                <Skeleton width="36px" height="36px" borderRadius="8px" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Workspace limits section */}
      <div className="workspace-limits-section">
        <Skeleton width="140px" height="18px" className="mb-3" />

        <div className="limits-grid">
          {Array.from({ length: 5 }, (_, index) => (
            <div key={index} className="limit-item">
              <div className="limit-header">
                <Skeleton width={index === 0 ? "40px" : index === 1 ? "80px" : index === 2 ? "120px" : index === 3 ? "60px" : "90px"} height="14px" />
                <div className="limit-values">
                  <Skeleton width="20px" height="13px" />
                  <span className="separator"> / </span>
                  <Skeleton width="30px" height="13px" />
                </div>
              </div>
              <div className="progress" style={{ height: '6px' }}>
                <Skeleton width="100%" height="6px" borderRadius="3px" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Invite section */}
      <div className="invite-section">
        <Skeleton width="120px" height="18px" className="mb-3" />
        <Skeleton width="100%" height="12px" className="mb-3" style={{ maxWidth: '400px' }} />

        <form className="invite-form">
          <Skeleton width="100%" height="44px" borderRadius="8px" style={{ flex: 1 }} />
          <Skeleton width="80px" height="44px" borderRadius="8px" />
          <Skeleton width="120px" height="44px" borderRadius="8px" />
        </form>
      </div>
    </div>
  </div>
);

// Skeleton for individual agent card - Updated to match AgentsList structure
export const AgentCardSkeleton = () => (
  <div className="agent-card skeleton-agent-card" style={{
    position: 'relative',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  }}>
    {/* Agent card header with gradient background */}
    <div className="agent-card-header skeleton-agent-card-header" style={{
      position: 'relative',
      background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%)',
      padding: '20px',
      borderRadius: '12px 12px 0 0',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      minHeight: '80px',
      color: 'white',
      overflow: 'hidden'
    }}>
      {/* Gradient overlay effects */}
      <div style={{
        content: '',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 150%, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 50%), radial-gradient(circle at 80% -20%, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 60%)',
        zIndex: 0
      }} />
      <div style={{
        content: '',
        position: 'absolute',
        top: '-15px',
        right: '-15px',
        width: '80px',
        height: '80px',
        background: 'rgba(255, 255, 255, 0.08)',
        borderRadius: '50%',
        zIndex: 0
      }} />

      {/* Agent icon skeleton */}
      <div className="agent-card-icon skeleton-agent-card-icon" style={{
        background: 'rgba(255, 255, 255, 0.2)',
        padding: '12px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(2px)',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        flexShrink: 0,
        position: 'relative',
        zIndex: 1,
        width: '48px',
        height: '48px'
      }}>
        <Skeleton width="24px" height="24px" borderRadius="50%" />
      </div>

      {/* Agent title skeleton */}
      <div style={{ flex: 1, position: 'relative', zIndex: 1 }}>
        <Skeleton width="120px" height="18px" />
      </div>
    </div>

    {/* Card content */}
    <div className="agent-card-content skeleton-agent-card-content" style={{
      padding: '24px 20px',
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    }}>
      {/* Agent type */}
      <div className="agent-card-type skeleton-agent-card-type" style={{
        fontSize: '14px',
        color: '#6b7280',
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <Skeleton width="16px" height="16px" />
        <Skeleton width="140px" height="14px" />
      </div>

      {/* Created date */}
      <div className="agent-card-created skeleton-agent-card-created" style={{
        color: '#9ca3af',
        fontSize: '13px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <Skeleton width="14px" height="14px" />
        <Skeleton width="140px" height="13px" />
      </div>

      {/* Status area - positioned at bottom */}
      <div className="agent-card-status skeleton-agent-card-status" style={{
        marginTop: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        paddingTop: '16px',
        borderTop: '1px solid #f3f4f6'
      }}>
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          <Skeleton width="8px" height="8px" borderRadius="50%" />
          <Skeleton width="50px" height="13px" />
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for create agent card - Updated to match AgentsList structure
export const CreateAgentCardSkeleton = () => (
  <div className="create-agent-card skeleton-create-agent-card" style={{
    height: '100%',
    borderRadius: '12px',
    border: '2px dashed #d1d5db',
    background: '#f9fafb',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '40px 24px',
    cursor: 'pointer',
    width: '100%'
  }}>
    <div className="create-agent-icon skeleton-create-agent-icon" style={{
      width: '56px',
      height: '56px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '20px',
      color: 'white',
      boxShadow: '0 6px 16px rgba(118, 56, 177, 0.25)'
    }}>
      <Skeleton width="28px" height="28px" borderRadius="50%" />
    </div>
    <div style={{
      marginBottom: '8px',
      fontSize: '16px',
      fontFamily: 'var(--font-primary)',
      fontWeight: 600,
      color: 'var(--primary)'
    }}>
      <Skeleton width="140px" height="16px" />
    </div>
    <div style={{
      marginBottom: 0,
      fontSize: '14px',
      color: '#6b7280',
      maxWidth: '240px',
      lineHeight: 1.4
    }}>
      <Skeleton width="200px" height="14px" />
    </div>
  </div>
);

// Skeleton for Quick Start Guide - Full width architecture
export const QuickStartGuideSkeleton = () => (
  <div className="quick-start-guide skeleton-quick-start-guide-skeleton" style={{
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  }}>
    {/* Header */}
    <div className="guide-header" style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px 24px',
      borderBottom: '1px solid #f3f4f6',
      background: '#ffffff'
    }}>
      <div className="d-flex align-items-center">
        <Skeleton width="150px" height="16px" className="me-2" />
        <Skeleton width="45px" height="22px" borderRadius="4px" />
      </div>
      <Skeleton width="36px" height="36px" borderRadius="4px" />
    </div>

    {/* Guide Body */}
    <div className="guide-body" style={{ background: '#ffffff' }}>
      {/* Steps */}
      <div className="guide-steps">
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className="guide-step" style={{
            borderBottom: index < 4 ? '1px solid #f3f4f6' : 'none',
            padding: '16px 24px'
          }}>
            <div className="d-flex align-items-start gap-3">
              <Skeleton width="24px" height="24px" borderRadius="50%" />
              <div className="step-content" style={{ flex: 1 }}>
                <Skeleton width="85%" height="14px" className="mb-2" />
                <Skeleton width="95%" height="13px" className="mb-1" />
                <Skeleton width="70%" height="13px" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="guide-footer" style={{
        background: '#f9fafb',
        borderTop: '1px solid #f3f4f6',
        padding: '16px 24px'
      }}>
        <div className="d-flex align-items-start">
          <Skeleton width="14px" height="14px" className="me-2" style={{ marginTop: '1px' }} />
          <Skeleton width="90%" height="12px" />
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for Workspace Info Panel - Full width architecture
export const WorkspaceInfoPanelSkeleton = () => (
  <div className="info-panel skeleton-workspace-info-panel-skeleton" style={{
    transition: 'all 0.3s ease',
    paddingBottom: '20px'
  }}>
    {/* Header */}
    <div className="info-panel-header" style={{
      marginBottom: '24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '12px',
      padding: 0
    }}>
      <Skeleton width="140px" height="16px" />
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Skeleton width="12px" height="12px" />
        <Skeleton width="120px" height="12px" />
      </div>
    </div>

    {/* Stats Grid */}
    <div className="info-panel-stats" style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '8px'
    }}>
      {Array.from({ length: 4 }, (_, index) => (
        <div key={index} className="info-stat-item" style={{
          borderRadius: '6px',
          overflow: 'hidden'
        }}>
          <div className="info-stat-content" style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px',
            background: '#fafbfc',
            border: '1px solid #e1e5e9',
            borderRadius: '6px',
            height: '-webkit-fill-available'
          }}>
            <Skeleton width="24px" height="24px" borderRadius="4px" className="me-2" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Skeleton width="70%" height="10px" className="mb-1" />
              <Skeleton width="50%" height="12px" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Skeleton for the entire AgentsList page - Updated to match current architecture
export const AgentsListSkeleton = ({ pageHeader = null }) => (
  <div className="agents-list-container skeleton-agents-list-container" style={{
    display: 'flex',
    flexDirection: 'column',
    margin: '0 auto',
    position: 'relative',
    overflow: 'hidden',
    height: 'calc(100dvh - 60px)'
  }}>
    {/* Render actual PageHeader if provided */}
    {pageHeader}

    {/* Main Content */}
    <div className="agents-content skeleton-agents-content" style={{
      display: 'flex',
      flex: 1,
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: '#ffffff',
      height: 'calc(100% - 100px)'
    }}>
      {/* Left Column: Agent Cards */}
      <div className="agents-main-content skeleton-agents-main-content" style={{
        flex: 1,
        overflowY: 'auto',
        padding: '24px 32px',
        backgroundColor: '#ffffff'
      }}>
        <div className="agents-grid skeleton-agents-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '24px'
        }}>
          {/* Agent cards */}
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className="agent-card-wrapper skeleton-agent-card-wrapper" style={{
              display: 'flex'
            }}>
              <AgentCardSkeleton />
            </div>
          ))}

          {/* Create agent card */}
          <div className="agent-card-wrapper skeleton-agent-card-wrapper" style={{
            display: 'flex'
          }}>
            <CreateAgentCardSkeleton />
          </div>
        </div>
      </div>

      {/* Right Column: Quick Start Guide and Workspace Info */}
      <div className="agents-sidebar skeleton-agents-sidebar" style={{
        width: '25%',
        maxWidth: '25rem',
        minWidth: '400px',
        backgroundColor: '#ffffff',
        borderLeft: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        flexShrink: 0,
        padding: '24px'
      }}>
        <QuickStartGuideSkeleton />
        <WorkspaceInfoPanelSkeleton />
      </div>
    </div>
  </div>
);

// Skeleton for user loading state (simpler version)
export const UserLoadingSkeleton = () => (
  <div className="content-loading-container d-flex align-items-center justify-content-center" style={{ minHeight: 'calc(100vh - 64px)' }}>
    <div style={{ textAlign: 'center' }}>
      <Skeleton width="40px" height="40px" borderRadius="50%" className="mx-auto mb-3" />
      <Skeleton width="100px" height="16px" className="mx-auto" />
    </div>
  </div>
);

// Skeleton for Agent Chats page - Full width architecture
export const AgentChatsSkeleton = () => (
  <div className="chats-content skeleton-chats-content">
    {/* Sidebar Skeleton */}
    <div className="chat-sidebar">
      {/* Sidebar Header */}
      <div className="sidebar-header-chats" style={{
        padding: '1rem',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        backgroundColor: 'white'
      }}>
        {/* "Conversations" title */}
        <Skeleton width="125px" height="16px" />
        {/* "Show Filters" button */}
        <Skeleton width="105px" height="28px" borderRadius="4px" />
      </div>

      {/* Chat List */}
      <div className="sidebar-chats-list" style={{
        flex: 1,
        overflowY: 'auto',
        padding: '0.5rem 0'
      }}>
        {Array.from({ length: 8 }, (_, index) => (
          <div key={index} className="chat-sidebar-item" style={{
            padding: '0.75rem 1rem',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
          }}>
            <div className="sidebar-item-content" style={{
              display: 'flex',
              alignItems: 'flex-start'
            }}>
              {/* Avatar */}
              <div className="sidebar-item-avatar" style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                marginRight: '0.75rem',
                flexShrink: 0
              }}>
                <Skeleton width="36px" height="36px" borderRadius="50%" />
              </div>

              {/* Chat Details */}
              <div className="sidebar-item-details" style={{
                flex: 1,
                minWidth: 0
              }}>
                {/* Message preview */}
                <Skeleton
                  width={`${70 + Math.random() * 25}%`}
                  height="14px"
                  style={{ marginBottom: '0.25rem' }}
                />

                {/* Metadata (clock icon + timestamp) */}
                <div className="sidebar-item-metadata" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  <Skeleton width="12px" height="12px" />
                  <Skeleton width="85px" height="11px" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Main Chat Content Skeleton */}
    <div className="chat-main-content">
      <div className="selected-chat">
        <div className="chat-content-with-details">
          {/* Chat Messages Container */}
          <div className="chat-messages-container">
            {/* Date Separator */}
            <div className="chat-timestamp">
              <Skeleton width="80px" height="16px" borderRadius="8px" />
            </div>

            {/* Message Pairs */}
            {Array.from({ length: 3 }, (_, pairIndex) => (
              <div key={pairIndex} className="chat-conversation-group">
                {/* User Message */}
                <div className="chat-message user-message">
                  <div className="message-avatar">
                    <Skeleton width="40px" height="40px" borderRadius="30%" />
                  </div>
                  <div className="message-content">
                    <div className="message-header">
                      <div className="d-flex align-items-center">
                        <Skeleton width="40px" height="16px" className="me-2" />
                      </div>
                      <Skeleton width="80px" height="12px" />
                    </div>
                    <Skeleton width="90%" height="16px" className="mb-1" />
                    <Skeleton width="70%" height="16px" />
                  </div>
                </div>

                {/* Bot Message */}
                <div className="chat-message bot-message">
                  <div className="message-avatar">
                    <Skeleton width="40px" height="40px" borderRadius="30%" />
                  </div>
                  <div className="message-content">
                    <div className="message-header">
                      <div className="d-flex align-items-center">
                        <Skeleton width="50px" height="16px" className="me-2" />
                        <Skeleton width="60px" height="20px" borderRadius="10px" className="me-2" />
                        <Skeleton width="80px" height="20px" borderRadius="10px" />
                      </div>
                      <div className="d-flex align-items-center">
                        <Skeleton width="80px" height="12px" className="me-2" />
                        <Skeleton width="24px" height="24px" borderRadius="50%" />
                      </div>
                    </div>
                    <Skeleton width="100%" height="16px" className="mb-1" />
                    <Skeleton width="85%" height="16px" className="mb-1" />
                    <Skeleton width="60%" height="16px" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Chat Details Sidebar */}
          <div className="chat-details-sidebar">
            <div className="details-header">
              <Skeleton width="60px" height="16px" />
              <div className="details-header-actions">
                <Skeleton width="16px" height="16px" className="d-md-none me-2" />
                <Skeleton width="24px" height="24px" borderRadius="4px" />
              </div>
            </div>

            <div className="details-content">
              {/* Detail Sections */}
              {Array.from({ length: 8 }, (_, index) => (
                <div key={index} className="detail-section">
                  <Skeleton width="60px" height="12px" className="mb-1" />
                  <div className="detail-value">
                    <Skeleton width="16px" height="16px" className="me-2" />
                    <Skeleton width={`${60 + Math.random() * 80}px`} height="14px" />
                  </div>
                </div>
              ))}

              {/* Feedback Stats */}
              <div className="detail-section">
                <Skeleton width="60px" height="12px" className="mb-1" />
                <div className="feedback-stats">
                  <div className="feedback-stat">
                    <Skeleton width="16px" height="16px" className="me-1" />
                    <Skeleton width="20px" height="14px" />
                  </div>
                  <div className="feedback-stat">
                    <Skeleton width="16px" height="16px" className="me-1" />
                    <Skeleton width="20px" height="14px" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Scroll Buttons */}
        <div className="chat-scroll-buttons">
          <div className="scroll-btn">
            <Skeleton width="16px" height="16px" />
          </div>
          <div className="scroll-btn">
            <Skeleton width="16px" height="16px" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for chatbot customization page
export const ChatbotCustomizationSkeleton = ({ pageHeader }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="chatbot-customization-container skeleton-chatbot-customization-container">
      {/* Page Header (passed from parent) */}
      {pageHeader}

      {/* Main Content */}
      <div className="customization-content skeleton-customization-content">
        {/* Settings Panel */}
        <div className="customization-settings-panel skeleton-customization-settings-panel">
          <div className="settings-panel-inner skeleton-settings-panel-inner">
            {/* Tabs Container */}
            <div className="tabs-container skeleton-tabs-container" style={{
              padding: '16px 20px',
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              flexShrink: 0,
              backgroundColor: 'white'
            }}>
              <div className="modern-tab-selector skeleton-modern-tab-selector" style={{ width: '100%', position: 'relative' }}>
                <div style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  border: '1.5px solid rgba(229, 231, 235, 0.8)',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}>
                  <div className="current-tab-content skeleton-current-tab-content" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div className="tab-icon skeleton-tab-icon">
                      <Skeleton width="16px" height="16px" />
                    </div>
                    <Skeleton width="70px" height="14px" />
                  </div>
                  <Skeleton width="16px" height="16px" />
                </div>
              </div>
            </div>

            {/* Settings Content */}
            <div className="settings-content skeleton-settings-content" style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px'
            }}>
              <div className="settings-section skeleton-settings-section" style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Section Header - "Templates" title */}
                <div className="section-header skeleton-section-header" style={{ marginBottom: '24px' }}>
                  <Skeleton width="90px" height="18px" style={{ marginBottom: '0' }} />
                </div>

                {/* Template Options Grid - 9 templates (3x3) */}
                <div className="template-options skeleton-template-options" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  {Array.from({ length: 9 }, (_, index) => (
                    <div
                      key={index}
                      className="template-card skeleton-template-card"
                      style={{
                        borderRadius: '8px',
                        padding: '16px',
                        textAlign: 'center',
                        backgroundColor: '#ffffff',
                        border: index === 0 ? '2px solid var(--primary)' : '1.5px solid #e5e7eb',
                        boxShadow: index === 0 ? '0 4px 12px rgba(var(--primary-rgb), 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
                        position: 'relative'
                      }}
                    >
                      {/* Active indicator dot on first card */}
                      {index === 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          width: '12px',
                          height: '12px',
                          backgroundColor: 'var(--primary)',
                          borderRadius: '50%',
                          boxShadow: '0 0 0 2px rgba(var(--primary-rgb), 0.2)'
                        }} />
                      )}
                      <div className="template-color-preview skeleton-template-color-preview" style={{
                        width: '100%',
                        height: '60px',
                        borderRadius: '6px',
                        marginBottom: '12px',
                        boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)'
                      }}>
                        <Skeleton width="100%" height="60px" borderRadius="6px" />
                      </div>
                      <div className="template-name skeleton-template-name" style={{ textAlign: 'center' }}>
                        <Skeleton
                          width={
                            index === 0 ? '50px' :
                              index === 1 ? '60px' :
                                index === 2 ? '55px' :
                                  index === 3 ? '65px' :
                                    index === 4 ? '45px' :
                                      index === 5 ? '70px' :
                                        index === 6 ? '60px' :
                                          index === 7 ? '50px' :
                                            '55px'
                          }
                          height="14px"
                          style={{ margin: '0 auto' }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Section Divider */}
                <div className="section-divider skeleton-section-divider" style={{
                  borderTop: '1px solid #e5e7eb',
                  margin: '24px 0'
                }}></div>

                {/* Brand Settings Section */}
                <div className="section-header skeleton-section-header" style={{ marginBottom: '24px' }}>
                  <Skeleton width="150px" height="18px" style={{ marginBottom: '8px' }} />
                  <Skeleton width={isMobile ? '100%' : '420px'} height="14px" style={{ display: isMobile ? 'none' : 'block' }} />
                </div>

                {/* Brand Settings Fields - 2 column grid */}
                <div className="fields-row skeleton-fields-row" style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '20px',
                  marginBottom: '20px'
                }}>
                  {/* Chatbot Icon */}
                  <div className="form-field skeleton-form-field" style={{ marginBottom: 0 }}>
                    <Skeleton width="90px" height="14px" style={{ marginBottom: '8px' }} />
                    <div className="file-input-wrapper skeleton-file-input-wrapper" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 16px',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}>
                        <Skeleton width="16px" height="16px" style={{ marginRight: '8px' }} />
                        <Skeleton width="85px" height="14px" />
                      </div>
                      <button className="revert-button skeleton-revert-button" style={{
                        padding: '8px',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '32px',
                        minHeight: '32px'
                      }}>
                        <Skeleton width="14px" height="14px" />
                      </button>
                    </div>
                  </div>

                  {/* Chatbot Name */}
                  <div className="form-field skeleton-form-field" style={{ marginBottom: 0 }}>
                    <Skeleton width="100px" height="14px" style={{ marginBottom: '8px' }} />
                    <div className="field-container skeleton-field-container" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        flex: 1,
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        padding: '10px 12px',
                        backgroundColor: '#ffffff'
                      }}>
                        <Skeleton width="100%" height="20px" />
                      </div>
                      <button className="revert-button skeleton-revert-button" style={{
                        padding: '8px',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '32px',
                        minHeight: '32px'
                      }}>
                        <Skeleton width="14px" height="14px" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Section Divider */}
                <div className="section-divider skeleton-section-divider" style={{
                  borderTop: '1px solid #e5e7eb',
                  margin: '24px 0'
                }}></div>

                {/* Color Settings Subsection */}
                <div className="subsection skeleton-subsection" style={{ marginBottom: '32px' }}>
                  <Skeleton width="120px" height="16px" style={{ marginBottom: '16px' }} />

                  {/* 3 rows of color pickers (2 columns each) */}
                  {Array.from({ length: 3 }, (_, rowIndex) => (
                    <div key={rowIndex} className="fields-row skeleton-fields-row" style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                      gap: '20px',
                      marginBottom: '20px'
                    }}>
                      {/* Left color picker */}
                      <div className="form-field skeleton-form-field" style={{ marginBottom: 0 }}>
                        <Skeleton
                          width={rowIndex === 0 ? '130px' : rowIndex === 1 ? '110px' : '140px'}
                          height="14px"
                          style={{ marginBottom: '8px' }}
                        />
                        <div className="field-container skeleton-field-container" style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <div className="color-picker-wrapper skeleton-color-picker-wrapper" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flex: 1
                          }}>
                            <div className="color-input-group skeleton-color-input-group" style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              padding: '4px',
                              background: 'white',
                              minWidth: '120px'
                            }}>
                              <Skeleton width="32px" height="32px" borderRadius="4px" />
                              <Skeleton width="75px" height="13px" />
                            </div>
                          </div>
                          <button className="revert-button skeleton-revert-button" style={{
                            padding: '8px',
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px',
                            minHeight: '32px'
                          }}>
                            <Skeleton width="14px" height="14px" />
                          </button>
                        </div>
                      </div>

                      {/* Right color picker */}
                      <div className="form-field skeleton-form-field" style={{ marginBottom: 0 }}>
                        <Skeleton
                          width={rowIndex === 0 ? '120px' : rowIndex === 1 ? '105px' : '125px'}
                          height="14px"
                          style={{ marginBottom: '8px' }}
                        />
                        <div className="field-container skeleton-field-container" style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <div className="color-picker-wrapper skeleton-color-picker-wrapper" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flex: 1
                          }}>
                            <div className="color-input-group skeleton-color-input-group" style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              padding: '4px',
                              background: 'white',
                              minWidth: '120px'
                            }}>
                              <Skeleton width="32px" height="32px" borderRadius="4px" />
                              <Skeleton width="75px" height="13px" />
                            </div>
                          </div>
                          <button className="revert-button skeleton-revert-button" style={{
                            padding: '8px',
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px',
                            minHeight: '32px'
                          }}>
                            <Skeleton width="14px" height="14px" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Section Divider */}
                <div className="section-divider skeleton-section-divider" style={{
                  borderTop: '1px solid #e5e7eb',
                  margin: '24px 0'
                }}></div>

                {/* Size Settings Subsection */}
                <div className="subsection skeleton-subsection" style={{ marginBottom: '32px' }}>
                  <Skeleton width="110px" height="16px" style={{ marginBottom: '16px' }} />

                  {/* 2 rows of range sliders */}
                  {Array.from({ length: 2 }, (_, rowIndex) => (
                    <div key={rowIndex} className="fields-row skeleton-fields-row" style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                      gap: '20px',
                      marginBottom: '20px'
                    }}>
                      {/* Left range slider */}
                      <div className="form-field skeleton-form-field" style={{ marginBottom: 0 }}>
                        <div style={{ marginBottom: '8px' }}>
                          <Skeleton width={rowIndex === 0 ? '150px' : '145px'} height="14px" />
                        </div>
                        <div className="field-container skeleton-field-container" style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <div className="range-slider-container skeleton-range-slider-container" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            flex: 1
                          }}>
                            <Skeleton width="100%" height="6px" borderRadius="3px" style={{ flex: 1 }} />
                            <Skeleton width="80px" height="42px" borderRadius="8px" />
                          </div>
                          <button className="revert-button skeleton-revert-button" style={{
                            padding: '8px',
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px',
                            minHeight: '32px'
                          }}>
                            <Skeleton width="14px" height="14px" />
                          </button>
                        </div>
                      </div>

                      {/* Right range slider */}
                      <div className="form-field skeleton-form-field" style={{ marginBottom: 0 }}>
                        <div style={{ marginBottom: '8px' }}>
                          <Skeleton width={rowIndex === 0 ? '140px' : '155px'} height="14px" />
                        </div>
                        <div className="field-container skeleton-field-container" style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <div className="range-slider-container skeleton-range-slider-container" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            flex: 1
                          }}>
                            <Skeleton width="100%" height="6px" borderRadius="3px" style={{ flex: 1 }} />
                            <Skeleton width="80px" height="42px" borderRadius="8px" />
                          </div>
                          <button className="revert-button skeleton-revert-button" style={{
                            padding: '8px',
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px',
                            minHeight: '32px'
                          }}>
                            <Skeleton width="14px" height="14px" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Panel */}
        <div className="customization-preview-panel skeleton-customization-preview-panel" style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          position: 'relative'
        }}>
        </div>
      </div>
    </div>
  );
};

// Skeleton for chatbot personality page - Full width architecture
export const ChatbotPersonalitySkeleton = ({ pageHeader }) => (
  <div className="chatbot-personality-container skeleton-chatbot-personality-container">
    {/* Page Header (passed from parent) */}
    {pageHeader}

    {/* Main Content Area */}
    <div className="personality-content skeleton-personality-content" style={{
      display: 'flex',
      flex: 1,
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Sidebar */}
      <div className="personality-sidebar skeleton-personality-sidebar">
        {/* Sidebar actions - Create Template Button */}
        <div className="sidebar-actions-personality skeleton-sidebar-actions-personality" style={{
          padding: '1rem',
          flexShrink: 0,
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '100%',
            padding: '0.75rem 1rem',
            fontSize: '0.875rem',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            border: '1px solid #e5e7eb',
            backgroundColor: 'white'
          }}>
            <Skeleton width="16px" height="16px" />
            <Skeleton width="170px" height="14px" />
          </div>
        </div>

        {/* Template Selection Dropdown */}
        <div className="template-selection skeleton-template-selection" style={{
          padding: '1rem',
          flexShrink: 0
        }}>
          <div className="dropdown-toggle skeleton-dropdown-toggle" style={{
            borderRadius: '8px',
            padding: '0.75rem',
            backgroundColor: '#fff',
            border: '1.5px solid #e0e6ed',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
              {/* Active template icon wrapper */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '35px',
                height: '35px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                boxShadow: '0 4px 12px rgba(var(--primary-rgb), 0.2)',
                flexShrink: 0
              }}>
                <Skeleton width="20px" height="20px" />
              </div>
              {/* Template name */}
              <Skeleton width="120px" height="14px" />
            </div>
            {/* Dropdown chevron */}
            <Skeleton width="16px" height="16px" />
          </div>
        </div>

        {/* Template Info Section */}
        <div className="template-info skeleton-template-info d-none d-md-block" style={{
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Skeleton width="140px" height="16px" style={{ marginBottom: '0.5rem' }} />
          </div>
          <Skeleton width="100%" height="13px" style={{ marginTop: '0.5rem', marginBottom: '0.25rem' }} />
          <Skeleton width="90%" height="13px" />
        </div>
      </div>

      {/* Main Content Area */}
      <div className="personality-main-content skeleton-personality-main-content" style={{
        width: '100%',
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div className="main-content-wrapper skeleton-main-content-wrapper" style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1.5rem'
        }}>
          {/* Prompts Section */}
          <div className="prompts-section skeleton-prompts-section">
            {/* Prompts Header */}
            <div className="prompts-header skeleton-prompts-header" style={{ marginBottom: '1.5rem' }}>
              <Skeleton width="130px" height="16px" style={{ fontWeight: 600 }} />
            </div>

            {/* Prompts Container */}
            <div className="prompts-container skeleton-prompts-container">
              {/* Prompts List - 3 prompt items */}
              <div className="prompts-list skeleton-prompts-list" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                {Array.from({ length: 3 }, (_, idx) => (
                  <div key={idx} className="prompt-item skeleton-prompt-item" style={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '1rem',
                    transition: 'all 0.2s ease'
                  }}>
                    {/* Prompt Item Header */}
                    <div className="prompt-item-header skeleton-prompt-item-header" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.75rem'
                    }}>
                      {/* Prompt number */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.25rem 0.75rem',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '20px'
                      }}>
                        <Skeleton width="25px" height="14px" />
                      </div>
                      {/* Delete button */}
                      <Skeleton width="70px" height="30px" borderRadius="6px" />
                    </div>

                    {/* Prompt Textarea */}
                    <div className="prompt-textarea-wrapper skeleton-prompt-textarea-wrapper">
                      <div style={{
                        width: '100%',
                        minHeight: '100px',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        backgroundColor: '#f9fafb',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem'
                      }}>
                        <Skeleton width="95%" height="12px" style={{ marginBottom: '0.5rem' }} />
                        <Skeleton width="88%" height="12px" style={{ marginBottom: '0.5rem' }} />
                        <Skeleton width="92%" height="12px" style={{ marginBottom: '0.5rem' }} />
                        <Skeleton width="78%" height="12px" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add New Prompt Section */}
              <div className="add-prompt-section skeleton-add-prompt-section" style={{
                marginTop: '1.5rem',
                padding: '1.5rem',
                backgroundColor: '#f9fafb',
                border: '2px dashed #d1d5db',
                borderRadius: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem'
                }}>
                  <Skeleton width="24px" height="24px" borderRadius="50%" />
                  <Skeleton width="140px" height="16px" />
                </div>

                {/* New prompt textarea */}
                <div style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  marginBottom: '0.75rem'
                }}>
                  <Skeleton width="100%" height="12px" style={{ marginBottom: '0.5rem' }} />
                  <Skeleton width="85%" height="12px" />
                </div>

                {/* Add button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Skeleton width="100px" height="36px" borderRadius="6px" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for Voice Settings page - Full width architecture
export const VoiceSettingsSkeleton = ({ pageHeader }) => (
  <div className="voice-settings-container skeleton-voice-settings-container">
    {/* Page Header (passed from parent) */}
    {pageHeader}

    {/* Main Content Area */}
    <div className="voice-settings-content skeleton-voice-settings-content" style={{
      flex: 1,
      overflowY: 'auto',
      backgroundColor: '#ffffff',
      padding: '24px 32px'
    }}>

      {/* Info Banner */}
      <div className="voice-info-banner skeleton-voice-info-banner" style={{
        display: 'flex',
        alignItems: 'flex-start',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '32px'
      }}>
        <div className="info-icon skeleton-info-icon" style={{
          flexShrink: 0,
          width: '20px',
          height: '20px',
          marginRight: '12px',
          marginTop: '2px'
        }}>
          <Skeleton width="20px" height="20px" />
        </div>
        <div className="info-content skeleton-info-content" style={{ flex: 1 }}>
          <Skeleton width="200px" height="14px" style={{ marginBottom: '4px' }} />
          <Skeleton width="100%" height="13px" style={{ maxWidth: '560px' }} />
        </div>
      </div>

      {/* Model Selection Section */}
      <div className="model-selection-section skeleton-model-selection-section" style={{ marginBottom: '40px' }}>
        <div className="section-header skeleton-section-header" style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '4px' }}>
            <Skeleton width="180px" height="18px" />
          </div>
          <div>
            <Skeleton width="380px" height="14px" />
          </div>
        </div>

        {/* Model Cards Row */}
        <div className="row" style={{ rowGap: '12px', marginBottom: '20px' }}>
          {Array.from({ length: 2 }, (_, index) => (
            <div key={index} className="col-12 col-md-6">
              <div className="voice-model-card skeleton-voice-model-card" style={{
                border: index === 0 ? '1px solid #55297F' : '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                boxShadow: index === 0 ? '0 0 0 1px #55297F' : 'none',
                padding: '16px',
                height: '100%'
              }}>
                <div className="voice-model-card-header" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <div className="model-name-credits-row" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%'
                  }}>
                    <Skeleton width={index === 0 ? "160px" : "140px"} height="14px" />
                    <div className="model-card-badges" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Skeleton width="75px" height="22px" borderRadius="4px" />
                      {index === 0 && (
                        <div className="model-selected-badge" style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          backgroundColor: '#55297F',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: 600
                        }}>✓</div>
                      )}
                    </div>
                  </div>
                </div>
                <Skeleton width="100%" height="13px" style={{ marginBottom: '4px' }} />
                <Skeleton width={index === 0 ? "95%" : "88%"} height="13px" />
              </div>
            </div>
          ))}
        </div>

        {/* Credits Explanation */}
        <div className="credits-explanation skeleton-credits-explanation" style={{
          borderTop: '1px solid #f3f4f6',
          paddingTop: '16px',
          marginTop: '20px'
        }}>
          <div className="credits-info" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Skeleton width="14px" height="14px" />
            <Skeleton width="100%" height="13px" style={{ maxWidth: '580px' }} />
          </div>
        </div>
      </div>

      {/* Voice Selection Section */}
      <div className="voice-selection-section skeleton-voice-selection-section" style={{ marginBottom: '40px' }}>
        <div className="section-header skeleton-section-header" style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '4px' }}>
            <Skeleton width="140px" height="18px" />
          </div>
          <div>
            <Skeleton width="380px" height="14px" />
          </div>
        </div>

        {/* Voice Cards Grid - Matching Bootstrap Row xs={2} md={3} lg={4} */}
        <div className="row g-3">
          {(() => {
            // Define colors matching the voice array
            const voiceColors = [
              '#e5e7eb', // alloy - neutral gray
              '#dbeafe', // echo - blue
              '#fae8ff', // shimmer - purple/pink
              '#f3f4f6', // ash - gray
              '#fce7f3', // ballad - soft pink
              '#fed7d7', // coral - coral/orange-pink
              '#d1fae5', // sage - green
              '#e9d5ff', // verse - lavender
              '#e0f2fe', // marin - ocean blue
              '#fef3c7'  // cedar - brown/wood
            ];

            const nameWidths = ['50px', '45px', '65px', '35px', '55px', '50px', '45px', '50px', '55px', '50px'];

            return Array.from({ length: 10 }, (_, index) => (
              <div key={index} className="col-6 col-md-4 col-lg-3">
                <div className="voice-card skeleton-voice-card" style={{
                  border: index === 0 ? '1px solid #55297F' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  boxShadow: index === 0 ? '0 0 0 1px #55297F' : 'none',
                  padding: '16px',
                  textAlign: 'center',
                  position: 'relative',
                  height: '100%'
                }}>
                  {/* Selected Badge */}
                  {index === 0 && (
                    <div className="voice-selected-badge" style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#55297F',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: 600
                    }}>✓</div>
                  )}

                  {/* Voice Icon with color */}
                  <div className="voice-icon skeleton-voice-icon" style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: voiceColors[index],
                    margin: '0 auto 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Skeleton width="24px" height="24px" />
                  </div>

                  {/* Voice Name */}
                  <Skeleton width={nameWidths[index]} height="14px" style={{ margin: '0 auto 8px' }} />

                  {/* Gender Badge */}
                  <div style={{ marginBottom: '8px' }}>
                    <Skeleton width="50px" height="18px" borderRadius="12px" style={{ margin: '0 auto' }} />
                  </div>

                  {/* Voice Description */}
                  <Skeleton width="100%" height="12px" style={{ marginBottom: '4px' }} />
                  <Skeleton width="80%" height="12px" style={{ margin: '0 auto' }} />
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

    </div>
  </div>
);

// Skeleton for AI settings page - Full width architecture
export const AISettingsSkeleton = ({ pageHeader }) => (
  <div className="ai-settings-container skeleton-ai-settings-container">
    {/* Page Header (passed from parent) */}
    {pageHeader}

    {/* Main Content Area */}
    <div className="ai-settings-content skeleton-ai-settings-content" style={{
      flex: 1,
      overflowY: 'auto',
      backgroundColor: '#ffffff',
      padding: '24px 32px'
    }}>

      {/* Info Banner */}
      <div className="model-info-banner skeleton-model-info-banner" style={{
        display: 'flex',
        alignItems: 'flex-start',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '32px'
      }}>
        <div className="info-icon skeleton-info-icon" style={{
          flexShrink: 0,
          width: '20px',
          height: '20px',
          marginRight: '12px',
          marginTop: '2px'
        }}>
          <Skeleton width="20px" height="20px" />
        </div>
        <div className="info-content skeleton-info-content" style={{ flex: 1 }}>
          <Skeleton width="180px" height="14px" style={{ marginBottom: '4px' }} />
          <Skeleton width="100%" height="13px" style={{ maxWidth: '500px' }} />
        </div>
      </div>

      {/* Model Selection Section */}
      <div className="model-selection-section skeleton-model-selection-section" style={{ marginBottom: '40px' }}>
        <div className="section-header skeleton-section-header" style={{ marginBottom: '20px' }}>
          <Skeleton width="170px" height="18px" style={{ marginBottom: '4px' }} />
          <Skeleton width="320px" height="14px" />
        </div>

        <div className="ai-model-selection skeleton-ai-model-selection" style={{ marginBottom: '20px' }}>
          {/* Google Models */}
          <div className="model-company-section skeleton-model-company-section" style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <div className="company-icon skeleton-company-icon" style={{ marginRight: '8px' }}>
                <Skeleton width="24px" height="24px" />
              </div>
              <Skeleton width="60px" height="16px" />
            </div>
            <div className="row g-3">
              {Array.from({ length: 2 }, (_, index) => (
                <div key={index} className="col-12 col-md-6 col-lg-4">
                  <div className="model-card" style={{
                    border: index === 0 ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    boxShadow: index === 0 ? '0 0 0 1px #3b82f6' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    height: '100%'
                  }}>
                    <div style={{ padding: '16px' }}>
                      <div className="model-card-header" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '8px'
                      }}>
                        <Skeleton width={index === 0 ? '110px' : '95px'} height="14px" />
                        <div className="model-card-badges" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Skeleton width="50px" height="22px" borderRadius="11px" />
                          {index === 0 && (
                            <div className="model-selected-badge" style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              backgroundColor: '#10b981',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '10px',
                              fontWeight: 600
                            }}>✓</div>
                          )}
                        </div>
                      </div>
                      <Skeleton width="100%" height="13px" style={{ marginBottom: '4px' }} />
                      <Skeleton width="85%" height="13px" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* OpenAI Models */}
          <div className="model-company-section skeleton-model-company-section" style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <div className="company-icon skeleton-company-icon" style={{ marginRight: '8px' }}>
                <Skeleton width="24px" height="24px" />
              </div>
              <Skeleton width="70px" height="16px" />
            </div>
            <div className="row g-3">
              {Array.from({ length: 3 }, (_, index) => (
                <div key={index} className="col-12 col-md-6 col-lg-4">
                  <div className="model-card" style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    height: '100%'
                  }}>
                    <div style={{ padding: '16px' }}>
                      <div className="model-card-header" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '8px'
                      }}>
                        <Skeleton width={index === 0 ? '95px' : index === 1 ? '100px' : '105px'} height="14px" />
                        <div className="model-card-badges" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Skeleton width="48px" height="22px" borderRadius="11px" />
                        </div>
                      </div>
                      <Skeleton width="100%" height="13px" style={{ marginBottom: '4px' }} />
                      <Skeleton width="90%" height="13px" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Anthropic Models */}
          <div className="model-company-section skeleton-model-company-section" style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <div className="company-icon skeleton-company-icon" style={{ marginRight: '8px' }}>
                <Skeleton width="24px" height="24px" />
              </div>
              <Skeleton width="80px" height="16px" />
            </div>
            <div className="row g-3">
              {Array.from({ length: 2 }, (_, index) => (
                <div key={index} className="col-12 col-md-6 col-lg-4">
                  <div className="model-card" style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    height: '100%'
                  }}>
                    <div style={{ padding: '16px' }}>
                      <div className="model-card-header" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '8px'
                      }}>
                        <Skeleton width={index === 0 ? '115px' : '120px'} height="14px" />
                        <div className="model-card-badges" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Skeleton width="52px" height="22px" borderRadius="11px" />
                        </div>
                      </div>
                      <Skeleton width="100%" height="13px" style={{ marginBottom: '4px' }} />
                      <Skeleton width="80%" height="13px" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* xAI Models */}
          <div className="model-company-section skeleton-model-company-section" style={{ marginBottom: '0' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <div className="company-icon skeleton-company-icon" style={{ marginRight: '8px' }}>
                <Skeleton width="24px" height="24px" />
              </div>
              <Skeleton width="35px" height="16px" />
            </div>
            <div className="row g-3">
              {Array.from({ length: 2 }, (_, index) => (
                <div key={index} className="col-12 col-md-6 col-lg-4">
                  <div className="model-card" style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    height: '100%'
                  }}>
                    <div style={{ padding: '16px' }}>
                      <div className="model-card-header" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '8px'
                      }}>
                        <Skeleton width={index === 0 ? '90px' : '95px'} height="14px" />
                        <div className="model-card-badges" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Skeleton width="50px" height="22px" borderRadius="11px" />
                        </div>
                      </div>
                      <Skeleton width="100%" height="13px" style={{ marginBottom: '4px' }} />
                      <Skeleton width="75%" height="13px" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Credits Explanation */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          backgroundColor: '#f9fafb',
          borderRadius: '6px',
          border: '1px solid #e5e7eb'
        }}>
          <Skeleton width="14px" height="14px" />
          <Skeleton width="100%" height="13px" style={{ maxWidth: '520px' }} />
        </div>
      </div>

      {/* Temperature Section */}
      <div className="temperature-section skeleton-temperature-section" style={{ marginBottom: '24px' }}>
        <div className="section-header skeleton-section-header" style={{ marginBottom: '20px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '4px'
          }}>
            <Skeleton width="180px" height="18px" />
            <Skeleton width="35px" height="20px" borderRadius="4px" />
          </div>
          <Skeleton width="300px" height="14px" />
        </div>

        <div className="temperature-controls skeleton-temperature-controls" style={{
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '20px'
        }}>
          {/* Temperature Slider */}
          <div style={{ marginBottom: '16px' }}>
            <Skeleton width="100%" height="8px" borderRadius="4px" />
          </div>
          {/* Temperature Labels */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <Skeleton width="80px" height="12px" />
            <Skeleton width="80px" height="12px" />
          </div>
          {/* Temperature Help Text */}
          <div>
            <Skeleton width="100%" height="13px" style={{ marginBottom: '4px' }} />
            <Skeleton width="70%" height="13px" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for Agent Leads page - Following AgentChats design patterns
export const AgentLeadsSkeleton = ({ pageHeader }) => (
  <div className="agent-leads-container skeleton-agent-leads-container">
    {/* Page Header (passed from parent) */}
    {pageHeader}

    {/* Main Content */}
    <div className="leads-content skeleton-leads-content">
      {/* Sidebar for filters */}
      <div className="leads-sidebar skeleton-leads-sidebar">
        <div className="sidebar-header skeleton-sidebar-header">
          <Skeleton width="140px" height="20px" />
          {/* Mobile filter toggle button */}
          <div className="filter-toggle-btn d-md-none skeleton-filter-toggle-btn">
            <Skeleton width="16px" height="16px" className="me-1" />
            <Skeleton width="50px" height="14px" />
          </div>
        </div>

        {/* Filters section - Initially hidden on mobile */}
        <div className="sidebar-filters d-none d-md-block skeleton-sidebar-filters">
          <div className="filter-section skeleton-filter-section">
            {/* Search Filter */}
            <div className="filter-item mb-3 skeleton-filter-item">
              <div className="filter-label skeleton-filter-label">
                <Skeleton width="100px" height="14px" className="mb-2" />
              </div>
              <div className="position-relative skeleton-search-input">
                <Skeleton width="100%" height="42px" borderRadius="8px" />
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="date-range-filters skeleton-date-range-filters">
              <div className="filter-item skeleton-filter-item">
                <div className="filter-label skeleton-filter-label">
                  <Skeleton width="80px" height="14px" className="mb-2" />
                </div>
                <div className="date-picker-container skeleton-date-picker-container">
                  <Skeleton width="100%" height="42px" borderRadius="8px" />
                </div>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="filter-actions skeleton-filter-actions">
              <Skeleton width="100%" height="42px" borderRadius="8px" className="mb-2" />
              <Skeleton width="100%" height="42px" borderRadius="8px" />
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="leads-main-content skeleton-leads-main-content">
        <div className="leads-data-container skeleton-leads-data-container">
          {/* Header actions */}
          <div className="leads-data-header skeleton-leads-data-header">
            <div className="leads-data-title skeleton-leads-data-title">
              <div className="section-title-leads skeleton-section-title-leads">
                <Skeleton width="16px" height="16px" className="me-2" />
                <Skeleton width="100px" height="18px" />
                {/* Mobile leads count */}
                <div className="d-md-none ms-2 leads-count-mobile skeleton-leads-count-mobile">
                  <Skeleton width="30px" height="14px" />
                </div>
              </div>
              <div className="leads-count-info d-none d-md-block skeleton-leads-count-info">
                <Skeleton width="300px" height="14px" />
              </div>
            </div>
            <div className="leads-data-actions skeleton-leads-data-actions">
              <Skeleton width="100px" height="36px" borderRadius="6px" />
            </div>
          </div>

          {/* Table Skeleton */}
          <div className="table-responsive skeleton-table-responsive">
            <div className="leads-table skeleton-leads-table">
              {/* Table Header - Desktop only */}
              <div className="d-none d-md-block skeleton-table-header">
                <div style={{ display: 'flex', padding: '1rem 0.75rem', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                  <div style={{ flex: '1', padding: '0 0.75rem' }}>
                    <Skeleton width="16px" height="16px" className="me-2" style={{ display: 'inline-block' }} />
                    <Skeleton width="40px" height="14px" style={{ display: 'inline-block' }} />
                  </div>
                  <div style={{ flex: '1', padding: '0 0.75rem' }}>
                    <Skeleton width="16px" height="16px" className="me-2" style={{ display: 'inline-block' }} />
                    <Skeleton width="50px" height="14px" style={{ display: 'inline-block' }} />
                  </div>
                  <div style={{ flex: '1', padding: '0 0.75rem' }}>
                    <Skeleton width="16px" height="16px" className="me-2" style={{ display: 'inline-block' }} />
                    <Skeleton width="50px" height="14px" style={{ display: 'inline-block' }} />
                  </div>
                  <div style={{ flex: '1', padding: '0 0.75rem' }}>
                    <Skeleton width="16px" height="16px" className="me-2" style={{ display: 'inline-block' }} />
                    <Skeleton width="50px" height="14px" style={{ display: 'inline-block' }} />
                  </div>
                  <div style={{ flex: '1', padding: '0 0.75rem' }}>
                    <Skeleton width="16px" height="16px" className="me-2" style={{ display: 'inline-block' }} />
                    <Skeleton width="70px" height="14px" style={{ display: 'inline-block' }} />
                  </div>
                  <div style={{ flex: '1', padding: '0 0.75rem' }}>
                    <Skeleton width="16px" height="16px" className="me-2" style={{ display: 'inline-block' }} />
                    <Skeleton width="50px" height="14px" style={{ display: 'inline-block' }} />
                  </div>
                </div>
              </div>

              {/* Table Body */}
              <div className="skeleton-table-body">
                {Array.from({ length: 5 }, (_, index) => (
                  <div key={index} className="skeleton-table-row d-none d-md-flex" style={{ display: 'flex', padding: '1rem 0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ flex: '1', padding: '0 0.75rem' }}>
                      <Skeleton width="120px" height="24px" borderRadius="6px" />
                    </div>
                    <div style={{ flex: '1', padding: '0 0.75rem' }}>
                      <Skeleton width="140px" height="16px" />
                    </div>
                    <div style={{ flex: '1', padding: '0 0.75rem' }}>
                      <Skeleton width="180px" height="16px" />
                    </div>
                    <div style={{ flex: '1', padding: '0 0.75rem' }}>
                      <Skeleton width="120px" height="16px" />
                    </div>
                    <div style={{ flex: '1', padding: '0 0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Skeleton width="14px" height="14px" />
                        <Skeleton width="100px" height="14px" />
                      </div>
                    </div>
                    <div style={{ flex: '1', padding: '0 0.75rem' }}>
                      <Skeleton width="80px" height="20px" borderRadius="6px" />
                    </div>
                  </div>
                ))}

                {/* Mobile Table Rows */}
                {Array.from({ length: 3 }, (_, index) => (
                  <div key={index} className="skeleton-mobile-table-row d-md-none" style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                    {/* Date row */}
                    <div style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>
                      <Skeleton width="50px" height="12px" style={{ marginRight: '0.75rem', minWidth: '80px' }} />
                      <Skeleton width="120px" height="20px" borderRadius="4px" />
                    </div>
                    {/* Name row */}
                    <div style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>
                      <Skeleton width="50px" height="12px" style={{ marginRight: '0.75rem', minWidth: '80px' }} />
                      <Skeleton width="140px" height="16px" />
                    </div>
                    {/* Email row */}
                    <div style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>
                      <Skeleton width="50px" height="12px" style={{ marginRight: '0.75rem', minWidth: '80px' }} />
                      <Skeleton width="180px" height="16px" />
                    </div>
                    {/* Phone row */}
                    <div style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>
                      <Skeleton width="50px" height="12px" style={{ marginRight: '0.75rem', minWidth: '80px' }} />
                      <Skeleton width="130px" height="16px" />
                    </div>
                    {/* Location row */}
                    <div style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>
                      <Skeleton width="70px" height="12px" style={{ marginRight: '0.75rem', minWidth: '80px' }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Skeleton width="14px" height="14px" />
                        <Skeleton width="100px" height="14px" />
                      </div>
                    </div>
                    {/* Source row */}
                    <div style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0' }}>
                      <Skeleton width="50px" height="12px" style={{ marginRight: '0.75rem', minWidth: '80px' }} />
                      <Skeleton width="80px" height="20px" borderRadius="6px" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for Agent Settings page - Full width architecture
export const AgentSettingsSkeleton = ({ pageHeader = null }) => (
  <div className="agent-settings-container skeleton-agent-settings-container">
    {/* Render actual PageHeader if provided */}
    {pageHeader}

    {/* Main Content Area */}
    <div className="settings-content skeleton-settings-content">
      {/* Settings Form Container */}
      <div className="settings-form-container skeleton-settings-form-container">
        <div className="settings-form-grid skeleton-settings-form-grid">
          {/* Chatbot Name Field */}
          <div className="settings-form-group skeleton-settings-form-group">
            <div className="settings-label skeleton-settings-label">
              <Skeleton width="100px" height="14px" />
            </div>
            <div className="settings-input-wrapper skeleton-settings-input-wrapper">
              <Skeleton width="100%" height="48px" borderRadius="8px" />
            </div>
            <div className="settings-help-text skeleton-settings-help-text">
              <Skeleton width="320px" height="13px" />
            </div>
          </div>

          {/* Allowed URLs Field */}
          <div className="settings-form-group skeleton-settings-form-group">
            <div className="settings-label skeleton-settings-label">
              <Skeleton width="90px" height="14px" />
            </div>
            <div className="settings-radio-wrapper skeleton-settings-radio-wrapper">
              <div className="radio-option skeleton-radio-option">
                <Skeleton width="16px" height="16px" borderRadius="50%" />
                <Skeleton width="60px" height="14px" />
              </div>
              <div className="radio-option skeleton-radio-option">
                <Skeleton width="16px" height="16px" borderRadius="50%" />
                <Skeleton width="90px" height="14px" />
              </div>
            </div>
            <div className="settings-textarea-wrapper skeleton-settings-textarea-wrapper">
              <Skeleton width="100%" height="90px" borderRadius="8px" />
            </div>
            <div className="settings-help-text skeleton-settings-help-text">
              <Skeleton width="100%" height="13px" className="mb-1" />
              <Skeleton width="60%" height="13px" />
            </div>
          </div>

          {/* Excluded URLs Field */}
          <div className="settings-form-group skeleton-settings-form-group">
            <div className="settings-label skeleton-settings-label">
              <Skeleton width="100px" height="14px" />
            </div>
            <div className="settings-textarea-wrapper skeleton-settings-textarea-wrapper">
              <Skeleton width="100%" height="90px" borderRadius="8px" />
            </div>
            <div className="settings-help-text skeleton-settings-help-text">
              <Skeleton width="100%" height="13px" className="mb-1" />
              <Skeleton width="70%" height="13px" />
            </div>
          </div>

          {/* Status Field */}
          <div className="settings-form-group skeleton-settings-form-group">
            <div className="settings-label skeleton-settings-label">
              <Skeleton width="50px" height="14px" />
            </div>
            <div className="settings-input-wrapper skeleton-settings-input-wrapper">
              <Skeleton width="100%" height="48px" borderRadius="8px" />
            </div>
            <div className="settings-help-text skeleton-settings-help-text">
              <Skeleton width="280px" height="13px" />
            </div>
          </div>
        </div>
      </div>

      {/* Success Alert Placeholder */}
      <div className="settings-success-alert-placeholder skeleton-settings-success-alert-placeholder">
        <Skeleton width="100%" height="0px" />
      </div>

      {/* Danger Section */}
      <div className="settings-danger-section skeleton-settings-danger-section">
        <div className="settings-danger-card skeleton-settings-danger-card">
          <div className="settings-danger-header skeleton-settings-danger-header">
            <div className="settings-danger-icon skeleton-settings-danger-icon">
              <Skeleton width="18px" height="18px" />
            </div>
            <div className="settings-danger-content skeleton-settings-danger-content">
              <div className="danger-title skeleton-danger-title">
                <Skeleton width="140px" height="16px" />
              </div>
              <div className="danger-description skeleton-danger-description">
                <Skeleton width="100%" height="14px" className="mb-1" />
                <Skeleton width="80%" height="14px" />
              </div>
            </div>
          </div>
          <div className="settings-danger-footer skeleton-settings-danger-footer">
            <Skeleton width="120px" height="40px" borderRadius="8px" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for Rate Limiting Settings page - Full width architecture
export const RateLimitingSettingsSkeleton = ({ pageHeader = null }) => (
  <div className="agent-settings-container skeleton-rate-limiting-container">
    {/* Render actual PageHeader if provided */}
    {pageHeader}

    {/* Main Content Area */}
    <div className="settings-content skeleton-settings-content">
      {/* Settings Form Container */}
      <div className="settings-form-container skeleton-settings-form-container">
        <div className="settings-form-grid skeleton-settings-form-grid">
          {/* Messages per Minute Field */}
          <div className="settings-form-group skeleton-settings-form-group">
            <div className="settings-label skeleton-settings-label">
              <Skeleton width="180px" height="14px" />
            </div>
            <div className="settings-input-wrapper skeleton-settings-input-wrapper">
              <div className="settings-input-group skeleton-settings-input-group">
                <Skeleton width="100%" height="48px" borderRadius="8px 0 0 8px" />
                <div className="settings-input-addon skeleton-settings-input-addon">
                  <Skeleton width="50px" height="14px" />
                </div>
              </div>
            </div>
            <div className="settings-help-text skeleton-settings-help-text">
              <Skeleton width="280px" height="13px" />
            </div>
          </div>

          {/* Burst Limit Field */}
          <div className="settings-form-group skeleton-settings-form-group">
            <div className="settings-label skeleton-settings-label">
              <Skeleton width="90px" height="14px" />
            </div>
            <div className="settings-input-wrapper skeleton-settings-input-wrapper">
              <div className="settings-input-group skeleton-settings-input-group">
                <Skeleton width="100%" height="48px" borderRadius="8px 0 0 8px" />
                <div className="settings-input-addon skeleton-settings-input-addon">
                  <Skeleton width="40px" height="14px" />
                </div>
              </div>
            </div>
            <div className="settings-help-text skeleton-settings-help-text">
              <Skeleton width="320px" height="13px" />
            </div>
          </div>

          {/* Messages per Hour Field */}
          <div className="settings-form-group skeleton-settings-form-group">
            <div className="settings-label skeleton-settings-label">
              <Skeleton width="180px" height="14px" />
            </div>
            <div className="settings-input-wrapper skeleton-settings-input-wrapper">
              <div className="settings-input-group skeleton-settings-input-group">
                <Skeleton width="100%" height="48px" borderRadius="8px 0 0 8px" />
                <div className="settings-input-addon skeleton-settings-input-addon">
                  <Skeleton width="45px" height="14px" />
                </div>
              </div>
            </div>
            <div className="settings-help-text skeleton-settings-help-text">
              <Skeleton width="0px" height="0px" />
            </div>
          </div>

          {/* Messages per Day Field */}
          <div className="settings-form-group skeleton-settings-form-group">
            <div className="settings-label skeleton-settings-label">
              <Skeleton width="170px" height="14px" />
            </div>
            <div className="settings-input-wrapper skeleton-settings-input-wrapper">
              <div className="settings-input-group skeleton-settings-input-group">
                <Skeleton width="100%" height="48px" borderRadius="8px 0 0 8px" />
                <div className="settings-input-addon skeleton-settings-input-addon">
                  <Skeleton width="55px" height="14px" />
                </div>
              </div>
            </div>
            <div className="settings-help-text skeleton-settings-help-text">
              <Skeleton width="0px" height="0px" />
            </div>
          </div>

          {/* Block Duration Field */}
          <div className="settings-form-group skeleton-settings-form-group">
            <div className="settings-label skeleton-settings-label">
              <Skeleton width="110px" height="14px" />
            </div>
            <div className="settings-input-wrapper skeleton-settings-input-wrapper">
              <div className="settings-input-group skeleton-settings-input-group">
                <Skeleton width="100%" height="48px" borderRadius="8px 0 0 8px" />
                <div className="settings-input-addon skeleton-settings-input-addon">
                  <Skeleton width="50px" height="14px" />
                </div>
              </div>
            </div>
            <div className="settings-help-text skeleton-settings-help-text">
              <Skeleton width="350px" height="13px" />
            </div>
          </div>
        </div>
      </div>

      {/* Success Alert Placeholder */}
      <div className="settings-success-alert-placeholder skeleton-settings-success-alert-placeholder">
        <Skeleton width="100%" height="0px" />
      </div>
    </div>
  </div>
);

// Skeleton for Agent Analytics page
export const AgentAnalyticsSkeleton = ({ pageHeader }) => (
  <div className="agent-analytics-container skeleton-agent-analytics-container">
    {/* Page Header (passed from parent) */}
    {pageHeader}

    {/* Analytics Content */}
    <div className="analytics-content skeleton-analytics-content">
      {/* General Overview Section */}
      <div className="analytics-section skeleton-analytics-section" style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: 'white' }}>
        <div className="section-header skeleton-section-header" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #f3f4f6', paddingBottom: '12px' }}>
            <Skeleton width="20px" height="20px" style={{ marginRight: '8px' }} />
            <Skeleton width="160px" height="24px" />
          </div>
        </div>

        <div className="charts-grid skeleton-charts-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px' }}>
          {/* 3 Chart Containers */}
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="chart-container skeleton-chart-container" style={{
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              overflow: 'hidden'
            }}>
              <div className="chart-header skeleton-chart-header" style={{ padding: '20px 20px 16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Skeleton width="18px" height="18px" />
                  <Skeleton width="100px" height="16px" />
                </div>
                <Skeleton width="80px" height="24px" borderRadius="4px" />
              </div>
              <div className="chart-wrapper skeleton-chart-wrapper" style={{ padding: '20px', height: '300px' }}>
                <Skeleton width="100%" height="100%" borderRadius="6px" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Geographic Distribution Section */}
      <div className="analytics-section skeleton-analytics-section" style={{ padding: '32px', borderBottom: '1px solid #e5e7eb', backgroundColor: 'white' }}>
        <div className="section-header skeleton-section-header" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #f3f4f6', paddingBottom: '12px' }}>
            <Skeleton width="20px" height="20px" style={{ marginRight: '8px' }} />
            <Skeleton width="180px" height="24px" />
          </div>
        </div>

        <div className="geographic-grid skeleton-geographic-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
          {/* Map Container */}
          <div className="map-container skeleton-map-container" style={{
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            minHeight: '400px'
          }}>
            <div className="map-wrapper skeleton-map-wrapper" style={{ padding: '20px', height: '100%', minHeight: '400px' }}>
              <Skeleton width="100%" height="100%" borderRadius="6px" />
            </div>
          </div>

          {/* Country List Container */}
          <div className="country-list-container skeleton-country-list-container" style={{
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div className="country-list-header skeleton-country-list-header" style={{ padding: '20px 20px 16px 20px', borderBottom: '1px solid #f3f4f6' }}>
              <Skeleton width="160px" height="16px" />
            </div>
            <div className="country-list-scrollable skeleton-country-list-scrollable" style={{ flex: 1, padding: '0' }}>
              {Array.from({ length: 8 }, (_, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderBottom: index < 7 ? '1px solid #f3f4f6' : 'none' }}>
                  <Skeleton width="100px" height="14px" />
                  <Skeleton width="40px" height="20px" borderRadius="4px" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Breakdown Section */}
      <div className="analytics-section skeleton-analytics-section" style={{ padding: '32px', backgroundColor: 'white' }}>
        <div className="section-header skeleton-section-header" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #f3f4f6', paddingBottom: '12px' }}>
            <Skeleton width="20px" height="20px" style={{ marginRight: '8px' }} />
            <Skeleton width="160px" height="24px" />
          </div>
        </div>

        <div className="breakdown-grid skeleton-breakdown-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* 2 Bar Chart Containers */}
          {Array.from({ length: 2 }, (_, index) => (
            <div key={index} className="chart-container skeleton-chart-container" style={{
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              overflow: 'hidden'
            }}>
              <div className="chart-header skeleton-chart-header" style={{ padding: '20px 20px 16px 20px', borderBottom: '1px solid #f3f4f6' }}>
                <Skeleton width="160px" height="16px" />
              </div>
              <div className="chart-wrapper skeleton-chart-wrapper" style={{ padding: '20px', height: '300px' }}>
                <Skeleton width="100%" height="100%" borderRadius="6px" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ========================================
// Training Pages Skeleton Loaders
// ========================================

// Skeleton for Training Card Item
export const TrainingCardItemSkeleton = ({ showAssignments = false }) => (
  <div className="training-card skeleton-training-card">
    <div className="py-3">
      <div className="d-flex align-items-start">
        <div className="flex-grow-1" style={{ minWidth: 0, overflow: 'hidden' }}>
          <div className="mb-2">
            <Skeleton width="80%" height="18px" />
          </div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Skeleton width="120px" height="16px" />
            <Skeleton width="100px" height="14px" />
          </div>
          {showAssignments && (
            <div className="mt-2">
              <Skeleton width="80px" height="12px" className="mb-1" />
              <div className="d-flex flex-wrap gap-1">
                <Skeleton width="100px" height="24px" borderRadius="12px" />
                <Skeleton width="80px" height="24px" borderRadius="12px" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for Training List (Files, Text, QA tabs)
export const TrainingContentListSkeleton = ({ count = 5, showSearch = true }) => (
  <div className="training-items-container skeleton-training-items-container">
    {/* Search Input */}
    {showSearch && (
      <div className="mb-3 skeleton-search-input">
        <Skeleton width="100%" height="44px" borderRadius="8px" />
      </div>
    )}

    {Array.from({ length: count }, (_, index) => (
      <TrainingCardItemSkeleton key={index} showAssignments={true} />
    ))}
    {/* Pagination */}
    <div className="pagination-wrapper skeleton-pagination-wrapper mt-4">
      <div className="d-flex justify-content-center gap-1">
        <Skeleton width="40px" height="40px" borderRadius="8px" />
        <Skeleton width="40px" height="40px" borderRadius="8px" />
        <Skeleton width="40px" height="40px" borderRadius="8px" />
        <Skeleton width="40px" height="40px" borderRadius="8px" />
        <Skeleton width="40px" height="40px" borderRadius="8px" />
      </div>
    </div>
  </div>
);

// Skeleton for Training Websites Tab (4 collapsed website cards)
export const TrainingWebsitesSkeleton = ({ count = 4 }) => (
  <div className="training-websites-container skeleton-training-websites-container">
    {/* Search Input */}
    <div className="mb-3 skeleton-search-input">
      <Skeleton width="100%" height="44px" borderRadius="8px" />
    </div>

    {/* Website Cards (Collapsed State) */}
    {Array.from({ length: count }, (_, index) => (
      <div key={index} className="training-card skeleton-website-card mb-3" style={{
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        overflow: 'hidden',
        background: '#ffffff'
      }}>
        {/* Card Header - Collapsed State */}
        <div className="card-header d-flex justify-content-between align-items-center" style={{
          padding: '16px',
          background: '#ffffff',
          borderBottom: 'none'
        }}>
          {/* Left side: Chevron + Website Title */}
          <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
            {/* Chevron Right Icon (collapsed indicator) */}
            <Skeleton width="16px" height="16px" className="me-2" />
            {/* Website Title */}
            <Skeleton width="60%" height="18px" />
          </div>

          {/* Right side: Action Buttons */}
          <div className="d-flex gap-2 website-item-actions" style={{ marginLeft: '12px' }}>
            {/* Sync Website Button */}
            <Skeleton width="120px" height="38px" borderRadius="6px" className="d-none d-md-block" />
            <Skeleton width="38px" height="38px" borderRadius="6px" className="d-block d-md-none" />

            {/* Sync Links Button */}
            <Skeleton width="100px" height="38px" borderRadius="6px" className="d-none d-md-block" />
            <Skeleton width="38px" height="38px" borderRadius="6px" className="d-block d-md-none" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// Skeleton for Workspace Training Main Page (with page header and sidebar)
export const WorkspaceTrainingSkeleton = ({ pageHeader, contentType = 'default' }) => (
  <div className="agent-training-container skeleton-training-page-container">
    {/* Page Header - render actual component if provided */}
    {pageHeader}

    {/* Content Area */}
    <div className="training-content skeleton-training-content">
      {/* Mobile - QuickStartGuide and WorkspaceInfoPanel above content */}
      <div className="d-block d-md-none training-sidebar">
        <QuickStartGuideSkeleton />
        <WorkspaceInfoPanelSkeleton />
      </div>

      <div className="training-layout skeleton-training-layout">
        {/* Main Content */}
        <div className="training-main-content skeleton-training-main-content">
          {/* Action Buttons */}
          <div className="skeleton-training-action-buttons mb-4">
            <div className="d-flex gap-2">
              <Skeleton width="120px" height="38px" borderRadius="6px" />
              <Skeleton width="100px" height="38px" borderRadius="6px" />
            </div>
            <div className="d-flex gap-2">
              <Skeleton width="80px" height="38px" borderRadius="6px" />
              <Skeleton width="120px" height="38px" borderRadius="6px" />
            </div>
          </div>

          {/* Training Content - Show different skeleton based on contentType */}
          {contentType === 'website' ? (
            <TrainingWebsitesSkeleton count={4} />
          ) : (
            <TrainingContentListSkeleton count={5} showSearch={true} />
          )}
        </div>

        {/* Sidebar - Desktop only */}
        <div className="training-sidebar skeleton-training-desktop-sidebar d-none d-lg-block">
          <WorkspaceInfoPanelSkeleton />
          <QuickStartGuideSkeleton />
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for Social Media Main page - Full width architecture
export const SocialMediaMainSkeleton = ({ pageHeader = null }) => (
  <div className="social-media-main skeleton-social-media-main">
    {/* Render actual PageHeader if provided */}
    {pageHeader}

    {/* Main Content */}
    <div className="main-content-social-media skeleton-main-content-social-media">
      {/* Action Header with Search, Filter, and Buttons */}
      <div className="action-header skeleton-action-header">
        <div className="search-filter-container skeleton-search-filter-container">
          {/* Search Input */}
          <Skeleton width="100%" height="42px" borderRadius="8px" />
          {/* Platform Filter - Desktop */}
          <Skeleton width="250px" height="42px" borderRadius="8px" className="d-none d-md-block" />
          {/* Platform Filter - Mobile */}
          <Skeleton width="100%" height="42px" borderRadius="8px" className="d-block d-md-none" />
        </div>
        <div className="action-buttons-social-media skeleton-action-buttons">
          {/* Schedule Posts Button - stacks on mobile via CSS */}
          <Skeleton width="160px" height="42px" borderRadius="8px" className="schedule-btn" />
          {/* Create New Post Button - stacks on mobile via CSS */}
          <Skeleton width="180px" height="42px" borderRadius="8px" className="create-new-btn" />
        </div>
      </div>

      {/* Content with Sidebar Layout */}
      <div className="content-with-sidebar skeleton-content-with-sidebar">
        {/* Main Posts Section */}
        <div className="main-posts-section skeleton-main-posts-section">
          <div className="posts-grid skeleton-posts-grid">
            <div className="row">
              {Array.from({ length: 6 }, (_, index) => (
                <div key={index} className="col-12 col-sm-6 col-md-6 col-lg-4" style={{ marginBottom: '16px' }}>
                  <div className="post-card skeleton-post-card" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                    {/* Compact Mockup Skeleton */}
                    <div style={{ position: 'relative', padding: '12px', background: '#fff' }}>
                      {/* Platform Badge (top-left) */}
                      <div style={{ position: 'absolute', top: '8px', left: '8px', zIndex: 10 }}>
                        <Skeleton width="70px" height="22px" borderRadius="12px" />
                      </div>

                      {/* Menu Button (top-right) */}
                      <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10 }}>
                        <Skeleton width="28px" height="28px" borderRadius="50%" />
                      </div>

                      {/* Header with Avatar and User Info */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '32px', marginBottom: '8px' }}>
                        <Skeleton width="32px" height="32px" borderRadius="50%" />
                        <div style={{ flex: 1 }}>
                          <Skeleton width="120px" height="13px" borderRadius="4px" className="mb-1" />
                        </div>
                      </div>

                      {/* Content Text */}
                      <div style={{ marginBottom: '8px' }}>
                        <Skeleton width="100%" height="13px" borderRadius="4px" className="mb-1" />
                        <Skeleton width="100%" height="13px" borderRadius="4px" className="mb-1" />
                        <Skeleton width="85%" height="13px" borderRadius="4px" />
                      </div>

                      {/* Image Placeholder */}
                      <Skeleton width="100%" height="120px" borderRadius="8px" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for Image Generation Main page - Full width architecture
export const ImageGenerationMainSkeleton = ({ pageHeader = null }) => (
  <div className="img-main-image-generation-main skeleton-image-generation-main">
    {/* Render actual PageHeader if provided */}
    {pageHeader}

    {/* Main Content */}
    <div className="img-main-main-content-images skeleton-main-content-images">
      {/* Action Header with Search, Filter, and Button */}
      <div className="img-main-action-header skeleton-action-header">
        <div className="img-main-search-filter-container skeleton-search-filter-container">
          {/* Search Input */}
          <Skeleton width="100%" height="42px" borderRadius="8px" />
          {/* Style Filter - Desktop */}
          <Skeleton width="250px" height="42px" borderRadius="8px" className="d-none d-md-block" />
          {/* Style Filter - Mobile */}
          <Skeleton width="100%" height="42px" borderRadius="8px" className="d-block d-md-none" />
        </div>
        {/* Create New Image Button - Desktop */}
        <Skeleton width="180px" height="42px" borderRadius="8px" className="d-none d-md-block img-main-create-new-btn" />
        {/* Create New Image Button - Mobile (full width) */}
        <Skeleton width="100%" height="42px" borderRadius="8px" className="d-block d-md-none img-main-create-new-btn" />
      </div>

      {/* Images Grid - Compact Card Design */}
      <div className="img-main-images-grid skeleton-images-grid">
        <div className="row">
          {Array.from({ length: 8 }, (_, index) => (
            <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-3" style={{ marginBottom: '20px' }}>
              <div className="img-main-compact-card skeleton-compact-card" style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e1e4e8' }}>
                {/* Image Container with overlays */}
                <div style={{ position: 'relative', height: '200px', background: '#f0f0f0' }}>
                  {/* Image placeholder */}
                  <Skeleton width="100%" height="200px" borderRadius="0" />

                  {/* Style Badge (top-left) */}
                  <div style={{ position: 'absolute', top: '8px', left: '8px', zIndex: 10 }}>
                    <Skeleton width="70px" height="22px" borderRadius="12px" />
                  </div>

                  {/* Menu Button (top-right) */}
                  <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10 }}>
                    <Skeleton width="28px" height="28px" borderRadius="50%" />
                  </div>

                  {/* Info Badges (bottom-left) */}
                  <div style={{ position: 'absolute', bottom: '8px', left: '8px', display: 'flex', gap: '6px', zIndex: 10 }}>
                    <Skeleton width="50px" height="18px" borderRadius="10px" />
                    <Skeleton width="60px" height="18px" borderRadius="10px" />
                  </div>
                </div>

                {/* Card Body - Minimal info */}
                <div style={{ padding: '12px', background: '#fff' }}>
                  {/* Prompt text - 2 lines */}
                  <div style={{ marginBottom: '8px' }}>
                    <Skeleton width="100%" height="13px" borderRadius="4px" className="mb-1" />
                    <Skeleton width="75%" height="13px" borderRadius="4px" />
                  </div>

                  {/* Timestamp */}
                  <Skeleton width="80px" height="12px" borderRadius="4px" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for Video Generation Main page - Full width architecture
export const VideoGenerationMainSkeleton = ({ pageHeader = null }) => (
  <div className="vid-main-video-generation-main skeleton-video-generation-main">
    {/* Render actual PageHeader if provided */}
    {pageHeader}

    {/* Main Content */}
    <div className="vid-main-main-content-videos skeleton-main-content-videos">
      {/* Action Header with Search, Filter and Create Button */}
      <div className="vid-main-action-header skeleton-action-header">
        <div className="vid-main-search-filter-container skeleton-search-filter-container">
          {/* Search Input with Icon */}
          <div className="vid-main-search-input skeleton-search-input" style={{ position: 'relative', flex: 1 }}>
            <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', zIndex: 1 }}>
              <Skeleton width="16px" height="16px" />
            </div>
            <Skeleton width="100%" height="42px" borderRadius="8px" />
          </div>
          {/* Style Filter Dropdown */}
          <div className="vid-main-style-filter skeleton-style-filter">
            <Skeleton width="200px" height="42px" borderRadius="8px" />
          </div>
        </div>
        {/* Create New Video Button */}
        <div className="skeleton-create-new-btn">
          <Skeleton width="180px" height="42px" borderRadius="8px" />
        </div>
      </div>

      {/* Videos Grid - Compact Card Design */}
      <div className="vid-main-videos-grid skeleton-videos-grid">
        <div className="row">
          {Array.from({ length: 8 }, (_, index) => (
            <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-3" style={{ marginBottom: '20px' }}>
              <div className="vid-main-compact-card skeleton-compact-card" style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e1e4e8' }}>
                {/* Video Container with overlays */}
                <div style={{ position: 'relative', height: '200px', background: '#0a0a0a' }}>
                  {/* Thumbnail placeholder */}
                  <Skeleton width="100%" height="200px" borderRadius="0" />

                  {/* Play Button Overlay (center) */}
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
                    <Skeleton width="50px" height="50px" borderRadius="50%" />
                  </div>

                  {/* Style Badge (top-left) */}
                  <div style={{ position: 'absolute', top: '8px', left: '8px', zIndex: 10 }}>
                    <Skeleton width="70px" height="22px" borderRadius="12px" />
                  </div>

                  {/* Menu Button (top-right) */}
                  <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10 }}>
                    <Skeleton width="28px" height="28px" borderRadius="50%" />
                  </div>

                  {/* Info Badges (bottom-left) */}
                  <div style={{ position: 'absolute', bottom: '8px', left: '8px', display: 'flex', gap: '6px', zIndex: 10 }}>
                    <Skeleton width="40px" height="18px" borderRadius="10px" />
                    <Skeleton width="50px" height="18px" borderRadius="10px" />
                  </div>
                </div>

                {/* Card Body - Minimal info */}
                <div style={{ padding: '12px', background: '#fff' }}>
                  {/* Prompt text - 2 lines */}
                  <div style={{ marginBottom: '6px' }}>
                    <Skeleton width="100%" height="13px" borderRadius="4px" className="mb-1" />
                    <Skeleton width="70%" height="13px" borderRadius="4px" />
                  </div>

                  {/* Timestamp */}
                  <Skeleton width="80px" height="11px" borderRadius="4px" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for SEO Analysis Main page - Full width architecture
export const SEOAnalysisMainSkeleton = ({ pageHeader = null }) => (
  <div className="seo-main-analysis-main skeleton-seo-analysis-main">
    {/* Render actual PageHeader if provided */}
    {pageHeader}

    {/* Main Content */}
    <div className="seo-main-main-content skeleton-main-content">
      {/* Action Header with Search, Filter, and Button */}
      <div className="seo-main-action-header skeleton-action-header">
        <div className="seo-main-search-filter-container skeleton-search-filter-container">
          {/* Search Input */}
          <Skeleton width="100%" height="42px" borderRadius="8px" />
          {/* Country Filter */}
          <Skeleton width="250px" height="42px" borderRadius="8px" />
        </div>
        {/* New SEO Analysis Button - stacks on mobile via CSS */}
        <Skeleton width="180px" height="42px" borderRadius="8px" className="seo-main-create-new-btn" />
      </div>

      {/* Analyses Grid - Compact Card Design */}
      <div className="row seo-main-analyses-grid skeleton-analyses-grid">
        {Array.from({ length: 9 }, (_, index) => (
          <div key={index} className="col-12 col-sm-6 col-md-6 col-lg-4" style={{ marginBottom: '20px' }}>
            <div className="seo-main-compact-card skeleton-compact-card" style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e1e4e8' }}>
              {/* Header Section with Gradient Background */}
              <div style={{ position: 'relative', padding: '24px 16px', background: 'linear-gradient(135deg, #6F1C1B 0%, #8B2524 50%, #A52A2A 100%)', minHeight: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                {/* Country Badge (top-left) */}
                <div style={{ position: 'absolute', top: '10px', left: '10px' }}>
                  <Skeleton width="60px" height="22px" borderRadius="12px" style={{ opacity: 0.3 }} />
                </div>

                {/* Menu Button (top-right) */}
                <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                  <Skeleton width="28px" height="28px" borderRadius="50%" style={{ opacity: 0.3 }} />
                </div>

                {/* Domain Hero */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  <Skeleton width="24px" height="24px" borderRadius="50%" style={{ opacity: 0.4 }} />
                  <Skeleton width="140px" height="16px" borderRadius="4px" style={{ opacity: 0.4 }} />
                </div>

                {/* Timestamp (bottom-right) */}
                <div style={{ position: 'absolute', bottom: '8px', right: '10px' }}>
                  <Skeleton width="60px" height="10px" borderRadius="4px" style={{ opacity: 0.3 }} />
                </div>
              </div>

              {/* Metrics Strip */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', background: '#fff', borderBottom: '1px solid #e1e4e8' }}>
                {[1, 2, 3, 4].map((_, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 4px', borderRight: i < 3 ? '1px solid #e1e4e8' : 'none' }}>
                    <Skeleton width="30px" height="14px" borderRadius="4px" className="mb-1" />
                    <Skeleton width="40px" height="9px" borderRadius="4px" />
                  </div>
                ))}
              </div>

              {/* Keywords Strip */}
              <div style={{ display: 'flex', gap: '6px', padding: '10px 12px', background: '#f9fafb' }}>
                <Skeleton width="80px" height="22px" borderRadius="12px" />
                <Skeleton width="70px" height="22px" borderRadius="12px" />
                <Skeleton width="30px" height="22px" borderRadius="12px" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Skeleton for Lead Main page - Two-column layout with sidebar
export const LeadMainSkeleton = ({ pageHeader = null }) => (
  <div className="lead-main skeleton-lead-main">
    {/* Render actual PageHeader if provided */}
    {pageHeader}

    {/* Main Content */}
    <div className="main-content-lead skeleton-main-content-lead">
      {/* Action Header with Search, Filter, and Buttons */}
      <div className="lead-main-action-header skeleton-action-header">
        <div className="search-filter-container skeleton-search-filter-container">
          {/* Search Input */}
          <Skeleton width="100%" height="42px" borderRadius="8px" />
          {/* Type Filter */}
          <Skeleton width="250px" height="42px" borderRadius="8px" className="platform-filter" />
        </div>
        <div className="action-buttons-lead skeleton-action-buttons">
          {/* Create List Button - stacks on mobile via CSS */}
          <Skeleton width="150px" height="42px" borderRadius="8px" className="create-list-btn" />
          {/* Search Leads Button - stacks on mobile via CSS */}
          <Skeleton width="160px" height="42px" borderRadius="8px" className="search-lead-btn" />
        </div>
      </div>

      {/* Mobile Lists Button (shown only on mobile) */}
      <div className="mobile-lists-button-container skeleton-mobile-lists">
        <Skeleton width="100%" height="42px" borderRadius="6px" />
      </div>

      {/* Content with Sidebar Layout */}
      <div className="content-with-sidebar skeleton-content-with-sidebar">
        {/* Main Searches Section */}
        <div className="main-searches-section skeleton-main-searches-section">
          <div className="row searches-grid skeleton-searches-grid">
            {Array.from({ length: 9 }, (_, index) => (
              <div key={index} className="col-12 col-sm-6 col-md-6 col-lg-4" style={{ marginBottom: '16px' }}>
                {/* Compact Card Design */}
                <div className="lead-main-compact-card skeleton-compact-card" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                  {/* Gradient Header Section */}
                  <div className="lead-main-compact-header" style={{
                    position: 'relative',
                    padding: '24px 16px',
                    background: 'linear-gradient(135deg, #1A562D 0%, #2d7a4f 50%, #3d9960 100%)',
                    minHeight: '130px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {/* Type Badge - Top Left */}
                    <div style={{ position: 'absolute', top: '10px', left: '10px' }}>
                      <Skeleton width="70px" height="22px" borderRadius="12px" style={{ opacity: 0.3 }} />
                    </div>
                    {/* Menu Button - Top Right */}
                    <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                      <Skeleton width="28px" height="28px" borderRadius="50%" style={{ opacity: 0.3 }} />
                    </div>
                    {/* Result Hero - Center */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Skeleton width="28px" height="28px" borderRadius="50%" style={{ opacity: 0.3 }} />
                        <Skeleton width="60px" height="32px" borderRadius="6px" style={{ opacity: 0.3 }} />
                      </div>
                      <Skeleton width="80px" height="12px" borderRadius="4px" style={{ opacity: 0.3 }} />
                    </div>
                    {/* Status Badge - Bottom Left */}
                    <div style={{ position: 'absolute', bottom: '10px', left: '10px' }}>
                      <Skeleton width="60px" height="18px" borderRadius="10px" style={{ opacity: 0.3 }} />
                    </div>
                    {/* Timestamp - Bottom Right */}
                    <div style={{ position: 'absolute', bottom: '10px', right: '10px' }}>
                      <Skeleton width="80px" height="14px" borderRadius="4px" style={{ opacity: 0.3 }} />
                    </div>
                  </div>
                  {/* Filters Strip */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 12px',
                    background: '#f9fafb'
                  }}>
                    <Skeleton width="90px" height="24px" borderRadius="12px" />
                    <Skeleton width="70px" height="24px" borderRadius="12px" />
                    <Skeleton width="80px" height="24px" borderRadius="12px" />
                    <Skeleton width="40px" height="24px" borderRadius="12px" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for calendar view in Social Media Main - Full page structure
export const CalendarViewSkeleton = ({ pageHeader = null }) => (
  <div className="social-media-main skeleton-social-media-main">
    {/* Render actual PageHeader if provided */}
    {pageHeader}

    {/* Main Content */}
    <div className="main-content-social-media skeleton-main-content-social-media">
      {/* Action Header with Search, Filter, View Toggle, and Buttons */}
      <div className="action-header skeleton-action-header">
        <div className="search-filter-container skeleton-search-filter-container">
          {/* Search Input */}
          <Skeleton width="100%" height="42px" borderRadius="8px" />
          {/* Platform Filter - Desktop */}
          <Skeleton width="250px" height="42px" borderRadius="8px" className="d-none d-md-block" />
          {/* Platform Filter - Mobile */}
          <Skeleton width="100%" height="42px" borderRadius="8px" className="d-block d-md-none" />
        </div>

        {/* View Toggle Skeleton */}
        <div className="view-toggle-group">
          <Skeleton width="100px" height="38px" borderRadius="0" />
          <Skeleton width="80px" height="38px" borderRadius="0" />
        </div>

        <div className="action-buttons-social-media skeleton-action-buttons">
          {/* Schedule Posts Button */}
          <Skeleton width="160px" height="42px" borderRadius="8px" className="schedule-btn" />
          {/* Create New Post Button */}
          <Skeleton width="180px" height="42px" borderRadius="8px" className="create-new-btn" />
        </div>
      </div>

      {/* Calendar View Content */}
      <div className="content-with-sidebar skeleton-content-with-sidebar">
        <div className="main-posts-section skeleton-main-posts-section">
          <div className="calendar-view-container">
            {/* Calendar Header Skeleton - matches renderCalendarHeader */}
            <div className="calendar-header">
              <div className="calendar-nav" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* Today Button */}
                <Skeleton width="70px" height="36px" borderRadius="8px" />
                {/* Prev Button */}
                <Skeleton width="36px" height="36px" borderRadius="8px" />
                {/* Next Button */}
                <Skeleton width="36px" height="36px" borderRadius="8px" />
              </div>
              {/* Week Label */}
              <Skeleton width="180px" height="20px" borderRadius="4px" />
              {/* Empty placeholder for right side */}
              <div style={{ width: '100px' }} />
            </div>

            {/* Calendar Week Grid Skeleton - 7 day columns */}
            <div className="calendar-week-grid">
              {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => {
                // Vary the number of posts per day for realistic look
                const postCounts = [2, 1, 3, 0, 2, 1, 2];
                const numPosts = postCounts[dayIndex];

                return (
                  <div key={dayIndex} className="calendar-day-column">
                    {/* Day Header - e.g., "Dec 21" and "Sun" */}
                    <div className="calendar-day-header">
                      <div className="calendar-day-date">
                        <Skeleton width="50px" height="14px" borderRadius="3px" />
                      </div>
                      <div className="calendar-day-name">
                        <Skeleton width="28px" height="12px" borderRadius="3px" />
                      </div>
                    </div>

                    {/* Day Posts */}
                    <div className="calendar-day-posts">
                      {numPosts > 0 ? (
                        Array.from({ length: numPosts }, (_, postIndex) => (
                          <div key={postIndex} className="calendar-post-card" style={{ display: 'block' }}>
                            {/* Post Header - platform icon, name, time, menu */}
                            <div className="calendar-post-header">
                              <div className="calendar-post-platform">
                                {/* Platform Icon */}
                                <Skeleton width="22px" height="22px" borderRadius="4px" />
                                {/* Platform Name */}
                                <Skeleton width="60px" height="12px" borderRadius="3px" />
                              </div>
                              <div className="calendar-post-meta">
                                {/* Time */}
                                <Skeleton width="38px" height="11px" borderRadius="3px" />
                                {/* Menu Button */}
                                <Skeleton width="20px" height="20px" borderRadius="4px" />
                              </div>
                            </div>

                            {/* Post Preview Text */}
                            <div className="calendar-post-preview">
                              <Skeleton width="100%" height="12px" borderRadius="3px" className="mb-1" />
                              <Skeleton width="90%" height="12px" borderRadius="3px" className="mb-1" />
                              <Skeleton width="60%" height="12px" borderRadius="3px" />
                            </div>

                            {/* Post Thumbnail - show on some posts */}
                            {postIndex % 2 === 0 && (
                              <div className="calendar-post-thumbnail">
                                <Skeleton width="100%" height="80px" borderRadius="6px" />
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="calendar-day-empty" style={{ opacity: 0.5 }}>
                          <Skeleton width="100px" height="12px" borderRadius="3px" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for just the calendar week grid content (no headers) - content only
export const CalendarWeekGridSkeleton = () => (
  <div className="calendar-week-grid">
    {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => {
      const postCounts = [2, 1, 3, 0, 2, 1, 2];
      const numPosts = postCounts[dayIndex];
      return (
        <div key={dayIndex} className="calendar-day-column">
          <div className="calendar-day-header">
            <div className="calendar-day-date">
              <Skeleton width="50px" height="14px" borderRadius="3px" />
            </div>
            <div className="calendar-day-name">
              <Skeleton width="28px" height="12px" borderRadius="3px" />
            </div>
          </div>
          <div className="calendar-day-posts">
            {numPosts > 0 ? (
              Array.from({ length: numPosts }, (_, i) => (
                <div key={i} className="calendar-post-card" style={{ display: 'block' }}>
                  <div className="calendar-post-header">
                    <div className="calendar-post-platform">
                      <Skeleton width="22px" height="22px" borderRadius="4px" />
                      <Skeleton width="60px" height="12px" borderRadius="3px" />
                    </div>
                    <div className="calendar-post-meta">
                      <Skeleton width="38px" height="11px" borderRadius="3px" />
                      <Skeleton width="20px" height="20px" borderRadius="4px" />
                    </div>
                  </div>
                  <div className="calendar-post-preview">
                    <Skeleton width="100%" height="12px" borderRadius="3px" className="mb-1" />
                    <Skeleton width="90%" height="12px" borderRadius="3px" className="mb-1" />
                    <Skeleton width="60%" height="12px" borderRadius="3px" />
                  </div>
                  {i % 2 === 0 && (
                    <div className="calendar-post-thumbnail">
                      <Skeleton width="100%" height="80px" borderRadius="6px" />
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="calendar-day-empty" style={{ opacity: 0.5 }}>
                <Skeleton width="100px" height="12px" borderRadius="3px" />
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

// Skeleton for just the posts grid content (no headers) - content only
export const PostsGridSkeleton = () => (
  <Row className="posts-grid">
    {Array.from({ length: 6 }, (_, index) => (
      <Col key={index} style={{ marginBottom: '16px' }} xs={12} sm={6} md={6} lg={4}>
        <div className="post-card skeleton-post-card" style={{ borderRadius: '12px', overflow: 'hidden' }}>
          <div style={{ position: 'relative', padding: '12px', background: '#fff' }}>
            <div style={{ position: 'absolute', top: '8px', left: '8px', zIndex: 10 }}>
              <Skeleton width="70px" height="22px" borderRadius="12px" />
            </div>
            <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10 }}>
              <Skeleton width="28px" height="28px" borderRadius="50%" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '32px', marginBottom: '8px' }}>
              <Skeleton width="32px" height="32px" borderRadius="50%" />
              <Skeleton width="120px" height="13px" borderRadius="4px" />
            </div>
            <div style={{ marginBottom: '8px' }}>
              <Skeleton width="100%" height="13px" borderRadius="4px" className="mb-1" />
              <Skeleton width="100%" height="13px" borderRadius="4px" className="mb-1" />
              <Skeleton width="85%" height="13px" borderRadius="4px" />
            </div>
            <Skeleton width="100%" height="120px" borderRadius="8px" />
          </div>
        </div>
      </Col>
    ))}
  </Row>
);

// ============================================
// IMAGE GENERATION CALENDAR SKELETONS
// ============================================

// Skeleton for just the image calendar week grid content (no headers) - content only
export const ImageCalendarWeekGridSkeleton = () => (
  <div className="img-main-calendar-week-grid">
    {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => {
      const imageCounts = [2, 1, 3, 0, 2, 1, 2];
      const numImages = imageCounts[dayIndex];
      return (
        <div key={dayIndex} className="img-main-calendar-day-column">
          <div className="img-main-calendar-day-header">
            <div className="img-main-calendar-day-date">
              <Skeleton width="50px" height="14px" borderRadius="3px" />
            </div>
            <div className="img-main-calendar-day-name">
              <Skeleton width="28px" height="12px" borderRadius="3px" />
            </div>
          </div>
          <div className="img-main-calendar-day-images">
            {numImages > 0 ? (
              Array.from({ length: numImages }, (_, i) => (
                <div key={i} className="img-main-calendar-image-card" style={{ display: 'block' }}>
                  <div className="img-main-calendar-image-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <Skeleton width="60px" height="14px" borderRadius="3px" />
                    <Skeleton width="35px" height="12px" borderRadius="3px" />
                  </div>
                  <div className="img-main-calendar-image-thumbnail" style={{ marginBottom: '6px' }}>
                    <Skeleton width="100%" height="160px" borderRadius="6px" />
                  </div>
                  <div className="img-main-calendar-image-prompt">
                    <Skeleton width="100%" height="12px" borderRadius="3px" className="mb-1" />
                    <Skeleton width="70%" height="12px" borderRadius="3px" />
                  </div>
                </div>
              ))
            ) : (
              <div className="img-main-calendar-day-empty" style={{ opacity: 0.5 }}>
                <Skeleton width="80px" height="12px" borderRadius="3px" />
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

// Skeleton for images grid content (no headers) - content only
export const ImagesGridSkeleton = () => (
  <Row className="img-main-images-grid">
    {Array.from({ length: 8 }, (_, index) => (
      <Col key={index} style={{ marginBottom: '20px' }} xs={12} sm={6} md={4} lg={3}>
        <div className="img-main-compact-card skeleton-compact-card" style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb', background: 'white' }}>
          <div className="img-main-compact-image-container" style={{ position: 'relative', height: '200px' }}>
            <Skeleton width="100%" height="200px" />
            <div style={{ position: 'absolute', top: '8px', left: '8px' }}>
              <Skeleton width="70px" height="20px" borderRadius="12px" />
            </div>
            <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
              <Skeleton width="28px" height="28px" borderRadius="50%" />
            </div>
          </div>
          <div className="img-main-compact-body" style={{ padding: '12px' }}>
            <Skeleton width="100%" height="13px" borderRadius="4px" className="mb-1" />
            <Skeleton width="85%" height="13px" borderRadius="4px" className="mb-2" />
            <Skeleton width="80px" height="11px" borderRadius="3px" />
          </div>
        </div>
      </Col>
    ))}
  </Row>
);

// ============================================
// VIDEO GENERATION CALENDAR SKELETONS
// ============================================

// Skeleton for just the video calendar week grid content (no headers) - content only
export const VideoCalendarWeekGridSkeleton = () => (
  <div className="vid-main-calendar-week-grid">
    {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => {
      const videoCounts = [1, 2, 1, 0, 2, 1, 1];
      const numVideos = videoCounts[dayIndex];
      return (
        <div key={dayIndex} className="vid-main-calendar-day-column">
          <div className="vid-main-calendar-day-header">
            <div className="vid-main-calendar-day-date">
              <Skeleton width="50px" height="14px" borderRadius="3px" />
            </div>
            <div className="vid-main-calendar-day-name">
              <Skeleton width="28px" height="12px" borderRadius="3px" />
            </div>
          </div>
          <div className="vid-main-calendar-day-videos">
            {numVideos > 0 ? (
              Array.from({ length: numVideos }, (_, i) => (
                <div key={i} className="vid-main-calendar-video-card" style={{ display: 'block' }}>
                  <div className="vid-main-calendar-video-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <Skeleton width="60px" height="14px" borderRadius="3px" />
                    <Skeleton width="35px" height="12px" borderRadius="3px" />
                  </div>
                  <div className="vid-main-calendar-video-thumbnail" style={{ marginBottom: '6px', position: 'relative' }}>
                    <Skeleton width="100%" height="80px" borderRadius="6px" />
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                      <Skeleton width="30px" height="30px" borderRadius="50%" />
                    </div>
                  </div>
                  <div className="vid-main-calendar-video-prompt">
                    <Skeleton width="100%" height="12px" borderRadius="3px" className="mb-1" />
                    <Skeleton width="70%" height="12px" borderRadius="3px" />
                  </div>
                </div>
              ))
            ) : (
              <div className="vid-main-calendar-day-empty" style={{ opacity: 0.5 }}>
                <Skeleton width="80px" height="12px" borderRadius="3px" />
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

// Skeleton for videos grid content (no headers) - content only
export const VideosGridSkeleton = () => (
  <Row className="vid-main-videos-grid">
    {Array.from({ length: 8 }, (_, index) => (
      <Col key={index} style={{ marginBottom: '20px' }} xs={12} sm={6} md={4} lg={3}>
        <div className="vid-main-compact-card skeleton-compact-card" style={{ borderRadius: '12px', overflow: 'hidden' }}>
          <div className="vid-main-compact-video-container" style={{ position: 'relative', height: '200px' }}>
            <Skeleton width="100%" height="200px" />
            <div style={{ position: 'absolute', top: '8px', left: '8px' }}>
              <Skeleton width="70px" height="20px" borderRadius="12px" />
            </div>
            <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
              <Skeleton width="28px" height="28px" borderRadius="50%" />
            </div>
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
              <Skeleton width="50px" height="50px" borderRadius="50%" />
            </div>
          </div>
          <div className="vid-main-compact-card-body" style={{ padding: '12px' }}>
            <Skeleton width="100%" height="13px" borderRadius="4px" className="mb-1" />
            <Skeleton width="85%" height="13px" borderRadius="4px" className="mb-2" />
            <Skeleton width="80px" height="11px" borderRadius="3px" />
          </div>
        </div>
      </Col>
    ))}
  </Row>
);

// ============================================
// LEAD MAIN CALENDAR SKELETONS
// ============================================

// Skeleton for lead calendar week grid content (no headers) - content only
export const LeadCalendarWeekGridSkeleton = () => (
  <div className="lead-main-calendar-week-grid">
    {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => {
      const searchCounts = [2, 1, 3, 0, 2, 1, 2];
      const numSearches = searchCounts[dayIndex];
      return (
        <div key={dayIndex} className="lead-main-calendar-day-column">
          <div className="lead-main-calendar-day-header">
            <div className="lead-main-calendar-day-date">
              <Skeleton width="50px" height="14px" borderRadius="3px" />
            </div>
            <div className="lead-main-calendar-day-name">
              <Skeleton width="28px" height="12px" borderRadius="3px" />
            </div>
          </div>
          <div className="lead-main-calendar-day-searches">
            {numSearches > 0 ? (
              Array.from({ length: numSearches }, (_, i) => (
                <div key={i} className="lead-main-calendar-search-card" style={{ padding: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <Skeleton width="60px" height="16px" borderRadius="4px" />
                    <Skeleton width="35px" height="12px" borderRadius="3px" />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Skeleton width="80px" height="16px" borderRadius="4px" style={{ margin: '0 auto' }} />
                  </div>
                </div>
              ))
            ) : (
              <div className="lead-main-calendar-day-empty" style={{ padding: '16px 8px', textAlign: 'center' }}>
                <Skeleton width="80px" height="12px" borderRadius="3px" style={{ margin: '0 auto' }} />
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

// Skeleton for lead searches grid content (no headers) - content only
export const LeadSearchesGridSkeleton = () => (
  <Row className="searches-grid">
    {Array.from({ length: 6 }, (_, index) => (
      <Col key={index} style={{ marginBottom: '16px' }} xs={12} sm={6} md={6} lg={4}>
        <div className="lead-main-compact-card skeleton-compact-card" style={{ borderRadius: '12px', overflow: 'hidden' }}>
          {/* Header with gradient background */}
          <div className="lead-main-compact-header" style={{ position: 'relative', minHeight: '130px', background: 'linear-gradient(135deg, #1A562D 0%, #2d7a4f 50%, #3d9960 100%)' }}>
            {/* Type badge - top left */}
            <div style={{ position: 'absolute', top: '10px', left: '10px' }}>
              <Skeleton width="70px" height="20px" borderRadius="12px" baseColor="rgba(255,255,255,0.2)" highlightColor="rgba(255,255,255,0.3)" />
            </div>
            {/* Menu button - top right */}
            <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
              <Skeleton width="28px" height="28px" borderRadius="50%" baseColor="rgba(255,255,255,0.2)" highlightColor="rgba(255,255,255,0.3)" />
            </div>
            {/* Center hero */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', paddingTop: '20px' }}>
              <Skeleton width="80px" height="36px" borderRadius="4px" baseColor="rgba(255,255,255,0.2)" highlightColor="rgba(255,255,255,0.3)" />
              <Skeleton width="60px" height="12px" borderRadius="3px" baseColor="rgba(255,255,255,0.2)" highlightColor="rgba(255,255,255,0.3)" style={{ marginTop: '8px' }} />
            </div>
            {/* Status badge - bottom left */}
            <div style={{ position: 'absolute', bottom: '10px', left: '10px' }}>
              <Skeleton width="50px" height="16px" borderRadius="10px" baseColor="rgba(255,255,255,0.2)" highlightColor="rgba(255,255,255,0.3)" />
            </div>
            {/* Timestamp - bottom right */}
            <div style={{ position: 'absolute', bottom: '10px', right: '10px' }}>
              <Skeleton width="70px" height="12px" borderRadius="3px" baseColor="rgba(255,255,255,0.2)" highlightColor="rgba(255,255,255,0.3)" />
            </div>
          </div>
          {/* Filters strip */}
          <div className="lead-main-filters-strip" style={{ padding: '10px 12px', background: '#f9fafb', display: 'flex', gap: '6px' }}>
            <Skeleton width="80px" height="22px" borderRadius="12px" />
            <Skeleton width="90px" height="22px" borderRadius="12px" />
          </div>
        </div>
      </Col>
    ))}
  </Row>
);

// ============================================
// SEO ANALYSIS CALENDAR SKELETONS
// ============================================

// Skeleton for SEO analyses grid content (no headers) - content only
export const SEOAnalysesGridSkeleton = () => (
  <Row className="seo-main-analyses-grid">
    {Array.from({ length: 6 }, (_, index) => (
      <Col key={index} style={{ marginBottom: '20px' }} xs={12} sm={6} md={6} lg={4}>
        <div className="seo-main-compact-card skeleton-compact-card" style={{ borderRadius: '12px', overflow: 'hidden' }}>
          {/* Header with gradient background */}
          <div className="seo-main-compact-header" style={{ position: 'relative', minHeight: '120px', background: 'linear-gradient(135deg, #6F1C1B 0%, #8B2524 50%, #A52A2A 100%)' }}>
            {/* Country badge - top left */}
            <div style={{ position: 'absolute', top: '10px', left: '10px' }}>
              <Skeleton width="55px" height="20px" borderRadius="12px" baseColor="rgba(255,255,255,0.2)" highlightColor="rgba(255,255,255,0.3)" />
            </div>
            {/* Menu button - top right */}
            <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
              <Skeleton width="28px" height="28px" borderRadius="50%" baseColor="rgba(255,255,255,0.2)" highlightColor="rgba(255,255,255,0.3)" />
            </div>
            {/* Center hero - domain */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', paddingTop: '20px' }}>
              <Skeleton width="24px" height="24px" borderRadius="4px" baseColor="rgba(255,255,255,0.2)" highlightColor="rgba(255,255,255,0.3)" />
              <Skeleton width="100px" height="16px" borderRadius="3px" baseColor="rgba(255,255,255,0.2)" highlightColor="rgba(255,255,255,0.3)" style={{ marginTop: '8px' }} />
            </div>
            {/* Timestamp - bottom right */}
            <div style={{ position: 'absolute', bottom: '8px', right: '10px' }}>
              <Skeleton width="70px" height="12px" borderRadius="3px" baseColor="rgba(255,255,255,0.2)" highlightColor="rgba(255,255,255,0.3)" />
            </div>
          </div>
          {/* Metrics strip */}
          <div className="seo-main-metrics-strip" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderBottom: '1px solid #e1e4e8' }}>
            {[0, 1, 2, 3].map(i => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 4px', borderRight: i < 3 ? '1px solid #e1e4e8' : 'none' }}>
                <Skeleton width="30px" height="14px" borderRadius="3px" />
                <Skeleton width="25px" height="10px" borderRadius="2px" style={{ marginTop: '4px' }} />
              </div>
            ))}
          </div>
          {/* Keywords strip */}
          <div className="seo-main-keywords-strip" style={{ padding: '10px 12px', background: '#f9fafb', display: 'flex', gap: '6px' }}>
            <Skeleton width="70px" height="22px" borderRadius="12px" />
            <Skeleton width="60px" height="22px" borderRadius="12px" />
          </div>
        </div>
      </Col>
    ))}
  </Row>
);

// Skeleton for SEO calendar week grid content (no headers) - content only
export const SEOCalendarWeekGridSkeleton = () => (
  <div className="seo-main-calendar-week-grid">
    {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => {
      const analysisCounts = [1, 2, 0, 1, 2, 1, 0];
      const numAnalyses = analysisCounts[dayIndex];
      return (
        <div key={dayIndex} className="seo-main-calendar-day-column">
          <div className="seo-main-calendar-day-header">
            <div className="seo-main-calendar-day-date">
              <Skeleton width="50px" height="14px" borderRadius="3px" />
            </div>
            <div className="seo-main-calendar-day-name">
              <Skeleton width="28px" height="12px" borderRadius="3px" />
            </div>
          </div>
          <div className="seo-main-calendar-day-analyses">
            {numAnalyses > 0 ? (
              Array.from({ length: numAnalyses }, (_, i) => (
                <div key={i} className="seo-main-calendar-analysis-card" style={{ padding: '10px' }}>
                  {/* Header: country badge + time/menu */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <Skeleton width="45px" height="14px" borderRadius="4px" />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Skeleton width="32px" height="10px" borderRadius="3px" />
                      <Skeleton width="20px" height="20px" borderRadius="4px" />
                    </div>
                  </div>
                  {/* Domain name */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', margin: '16px 0' }}>
                    <Skeleton width="12px" height="12px" borderRadius="2px" />
                    <Skeleton width="80px" height="12px" borderRadius="3px" />
                  </div>
                  {/* Metrics 2x2 grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #f0f0f0' }}>
                    {[0, 1, 2, 3].map(j => (
                      <div key={j} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2px 0' }}>
                        <Skeleton width="24px" height="12px" borderRadius="3px" />
                        <Skeleton width="32px" height="8px" borderRadius="2px" style={{ marginTop: '2px' }} />
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="seo-main-calendar-day-empty" style={{ padding: '16px 8px', textAlign: 'center' }}>
                <Skeleton width="80px" height="12px" borderRadius="3px" style={{ margin: '0 auto' }} />
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

export default Skeleton;